/**
 * StockDetailPage — Seeking Alpha / Bloomberg-style deep-dive for any ticker.
 * Route: /app/stock/:ticker
 *
 * Tabs: Overview | Technicals | Fundamentals | Earnings | Alt Data | News
 */
import { useState, useEffect, useRef, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft, RefreshCw, ExternalLink, TrendingUp, TrendingDown,
  BarChart2, Activity, DollarSign, Users, Newspaper, Radio,
  Building2, Globe, ChevronRight, AlertTriangle, Loader2,
  Star, Zap, Shield, Eye, FileText,
  BookmarkPlus, Terminal, SlidersHorizontal, GitCompare, Check, Brain,
} from 'lucide-react'
import {
  createChart,
  LineSeries,
  ColorType,
  CrosshairMode,
  type IChartApi,
} from 'lightweight-charts'
import { clsx } from 'clsx'
import { useStockDetail } from '@/hooks/useStockDetail'
import { useStockFinancials } from '@/hooks/useStockFinancials'
import { useWatchlist } from '@/hooks/useWatchlist'
import { useRecentActivity } from '@/hooks/useRecentActivity'
import { getChartTechnicals, api } from '@/lib/api'
import TickerLink from '@/components/ui/TickerLink'
import { PortfolioFitBadge, PortfolioFitNote } from '@/components/ui/PortfolioFitBadge'
import type {
  StockDetailQuote,
  StockDetailRatings,
  StockDetailTechnicals,
  StockDetailFactorGrade,
  StockFinancials,
  OHLCVBar,
} from '@/lib/types'

// ── Helpers ──────────────────────────────────────────────────────────────────

function fmtPrice(n: number | null, currency = 'USD'): string {
  if (n == null) return 'N/A'
  const symbol = currency === 'GBP' ? '£' : currency === 'EUR' ? '€' : '$'
  return `${symbol}${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function fmtCap(n: number | null): string {
  if (n == null) return 'N/A'
  if (n >= 1e12) return `$${(n / 1e12).toFixed(2)}T`
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`
  return `$${n.toLocaleString()}`
}

function fmtVol(n: number | null): string {
  if (n == null) return 'N/A'
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`
  if (n >= 1e3) return `${(n / 1e3).toFixed(0)}K`
  return n.toString()
}

function fmtPct(n: number | null, decimals = 2): string {
  if (n == null) return 'N/A'
  return `${n >= 0 ? '+' : ''}${n.toFixed(decimals)}%`
}

function fmtDate(iso: string | null): string {
  if (!iso) return 'N/A'
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  } catch {
    return iso
  }
}

// ── Grade helpers ─────────────────────────────────────────────────────────────

function gradeColor(grade: string): string {
  switch (grade) {
    case 'A+': case 'A': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
    case 'A-': case 'B+': return 'bg-green-500/20 text-green-400 border-green-500/30'
    case 'B': return 'bg-sky-500/20 text-sky-400 border-sky-500/30'
    case 'B-': case 'C+': return 'bg-amber-500/20 text-amber-400 border-amber-500/30'
    case 'C': case 'D': return 'bg-orange-500/20 text-orange-400 border-orange-500/30'
    case 'F': return 'bg-rose-500/20 text-rose-400 border-rose-500/30'
    default: return 'bg-surface-hover text-text-secondary border-border'
  }
}

function ratingBg(r: number): string {
  if (r >= 4.0) return 'text-emerald-400'
  if (r >= 3.0) return 'text-sky-400'
  if (r >= 2.0) return 'text-amber-400'
  return 'text-rose-400'
}

function GradeBadge({ grade }: { grade: string | undefined }) {
  if (!grade) return <span className="text-text-muted text-xs">-</span>
  return (
    <span className={clsx(
      'text-[10px] font-bold px-1.5 py-0.5 rounded border',
      gradeColor(grade),
    )}>
      {grade}
    </span>
  )
}

// ── Trend badge ───────────────────────────────────────────────────────────────

function TrendBadge({ trend }: { trend: string }) {
  if (trend === 'BULLISH') {
    return (
      <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 text-xs font-semibold">
        <TrendingUp size={12} /> BULLISH
      </span>
    )
  }
  if (trend === 'BEARISH') {
    return (
      <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/15 text-rose-400 text-xs font-semibold">
        <TrendingDown size={12} /> BEARISH
      </span>
    )
  }
  return (
    <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-surface-hover text-text-secondary text-xs font-semibold">
      <Activity size={12} /> NEUTRAL
    </span>
  )
}

// ── RSI gauge ─────────────────────────────────────────────────────────────────

function RSIGauge({ rsi }: { rsi: number | null }) {
  if (rsi == null) return <span className="text-text-muted text-sm">N/A</span>

  const clamped = Math.max(0, Math.min(100, rsi))
  const pct = clamped

  let color = 'bg-sky-500'
  let label = 'Neutral'
  if (clamped >= 70) { color = 'bg-rose-500'; label = 'Overbought' }
  else if (clamped <= 30) { color = 'bg-emerald-500'; label = 'Oversold' }

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[11px] text-text-muted">
        <span>Oversold (30)</span>
        <span className="font-mono font-semibold text-text-primary">{rsi.toFixed(1)}</span>
        <span>Overbought (70)</span>
      </div>
      <div className="h-3 w-full rounded-full bg-surface-hover relative overflow-hidden">
        {/* Zones */}
        <div className="absolute inset-y-0 left-0 w-[30%] bg-emerald-500/20 rounded-l-full" />
        <div className="absolute inset-y-0 right-0 w-[30%] bg-rose-500/20 rounded-r-full" />
        {/* Pointer */}
        <div
          className={clsx('absolute top-1 bottom-1 w-2 rounded-full -translate-x-1/2', color)}
          style={{ left: `${pct}%` }}
        />
      </div>
      <p className={clsx(
        'text-[11px] font-semibold text-center',
        clamped >= 70 ? 'text-rose-400' : clamped <= 30 ? 'text-emerald-400' : 'text-sky-400',
      )}>
        {label}
      </p>
    </div>
  )
}

// ── SMA row ───────────────────────────────────────────────────────────────────

function SMARow({
  label, sma, price,
}: { label: string; sma: number | null; price: number | null }) {
  if (sma == null || price == null) {
    return (
      <div className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
        <span className="text-sm text-text-muted">{label}</span>
        <span className="text-sm text-text-muted">N/A</span>
      </div>
    )
  }
  const above = price > sma
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-border last:border-0">
      <span className="text-sm text-text-secondary">{label}</span>
      <div className="flex items-center gap-2">
        <span className="font-mono text-xs text-text-muted">${sma.toFixed(2)}</span>
        <span className={clsx(
          'text-[10px] font-semibold px-1.5 py-0.5 rounded',
          above ? 'bg-emerald-500/20 text-emerald-400' : 'bg-rose-500/20 text-rose-400',
        )}>
          {above ? 'ABOVE' : 'BELOW'}
        </span>
      </div>
    </div>
  )
}

// ── Mini chart ────────────────────────────────────────────────────────────────

const CHART_PERIODS = ['1M', '3M', '6M', '1Y', '2Y'] as const
type ChartPeriod = (typeof CHART_PERIODS)[number]

const PERIOD_API_MAP: Record<ChartPeriod, string> = {
  '1M': '1mo',
  '3M': '3mo',
  '6M': '6mo',
  '1Y': '1y',
  '2Y': '2y',
}

function MiniChart({ ticker, currency }: { ticker: string; currency: string }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<IChartApi | null>(null)
  const [activePeriod, setActivePeriod] = useState<ChartPeriod>('1Y')
  const [bars, setBars] = useState<OHLCVBar[]>([])
  const [chartLoading, setChartLoading] = useState(false)

  // Fetch bars whenever ticker or period changes
  useEffect(() => {
    if (!ticker) return
    setChartLoading(true)
    getChartTechnicals(ticker, PERIOD_API_MAP[activePeriod])
      .then(r => setBars(r.data || []))
      .catch(() => setBars([]))
      .finally(() => setChartLoading(false))
  }, [ticker, activePeriod])

  // Build / rebuild chart whenever bars change
  useEffect(() => {
    if (!containerRef.current || bars.length === 0) return

    // Destroy previous instance
    if (chartRef.current) {
      chartRef.current.remove()
      chartRef.current = null
    }

    const chart = createChart(containerRef.current, {
      width: containerRef.current.clientWidth,
      height: 300,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: '#9ca3af',
      },
      grid: {
        vertLines: { color: '#1f2937' },
        horzLines: { color: '#1f2937' },
      },
      crosshair: { mode: CrosshairMode.Normal },
      timeScale: { borderColor: '#374151' },
      rightPriceScale: { borderColor: '#374151' },
    })

    const series = chart.addSeries(LineSeries, {
      color: '#3b82f6',
      lineWidth: 2,
    })

    const sorted = [...bars].sort((a, b) => a.date.localeCompare(b.date))
    series.setData(sorted.map(b => ({ time: b.date as import('lightweight-charts').Time, value: b.close })))
    chart.timeScale().fitContent()
    chartRef.current = chart

    // Resize observer so the chart fills its container on layout changes
    const ro = new ResizeObserver(() => {
      if (containerRef.current && chartRef.current) {
        chartRef.current.applyOptions({ width: containerRef.current.clientWidth })
      }
    })
    ro.observe(containerRef.current)

    return () => {
      ro.disconnect()
      chart.remove()
      chartRef.current = null
    }
  }, [bars])

  return (
    <div className="card p-4">
      {/* Header row */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <BarChart2 size={14} className="text-blue-light" />
          <h3 className="font-semibold text-text-primary text-sm">Price History</h3>
        </div>
        {/* Period buttons */}
        <div className="flex gap-1">
          {CHART_PERIODS.map(p => (
            <button
              key={p}
              type="button"
              onClick={() => setActivePeriod(p)}
              className={clsx(
                'px-2 py-0.5 rounded text-[11px] font-semibold transition-colors',
                activePeriod === p
                  ? 'bg-blue/20 text-blue-light border border-blue/40'
                  : 'text-text-muted hover:text-text-secondary border border-transparent',
              )}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Chart area */}
      <div className="relative">
        {chartLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-surface-card/70 rounded z-10">
            <Loader2 size={20} className="animate-spin text-blue-light" />
          </div>
        )}
        {!chartLoading && bars.length === 0 && (
          <div className="flex items-center justify-center h-[300px] text-text-muted text-sm">
            No price data available
          </div>
        )}
        <div ref={containerRef} className="w-full" />
      </div>

      {/* Footer link */}
      <div className="mt-3 flex justify-end">
        <Link
          to={`/app/chart?ticker=${encodeURIComponent(ticker)}`}
          className="text-xs text-cyan-400 hover:text-cyan-300 transition-colors flex items-center gap-1"
        >
          Open full chart
          <ChevronRight size={12} />
        </Link>
      </div>
    </div>
  )
}

// ── Stat cell ─────────────────────────────────────────────────────────────────

function StatCell({ label, value, highlight }: { label: string; value: string; highlight?: string }) {
  return (
    <div className="card p-3">
      <div className="text-[11px] text-text-muted uppercase tracking-wider mb-1">{label}</div>
      <div className={clsx('font-mono text-sm font-semibold tabular', highlight ?? 'text-text-primary')}>
        {value}
      </div>
    </div>
  )
}

// ── Tab definitions ───────────────────────────────────────────────────────────

const TABS = ['Overview', 'Technicals', 'Fundamentals', 'Earnings', 'Financials', 'Catalysts', 'Alt Data', 'Signals', 'News'] as const
type Tab = (typeof TABS)[number]

// ── Overview Tab ─────────────────────────────────────────────────────────────

// ── Signal Intelligence section ──────────────────────────────────────────────

interface TickerSignal {
  source: string
  signal: string
  sentiment: number
  summary: string
  score?: number
  timestamp?: string
}

interface TickerSignalData {
  ticker: string
  convergence: string
  signal_count: number
  /** Backend sends the list of source names — there is no source_count field. */
  sources?: string[]
  net_sentiment: number
  agreement_ratio?: number | null
  conviction_quality?: { score: number; grade: string } | null
  directional_count?: number
  top_signals: TickerSignal[]
  /** Backend can 200 with an {error} body — treated as the error state. */
  error?: string | null
}

interface SmartMoneyData {
  smart_money_score: number | null
  dumb_money_score: number | null
}

function SignalIntelligenceSection({ ticker }: { ticker: string }) {
  const [signals, setSignals] = useState<TickerSignalData | null>(null)
  const [smartMoney, setSmartMoney] = useState<SmartMoneyData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    if (!ticker) return
    setLoading(true)
    setError(false)
    Promise.allSettled([
      api.get(`/api/signals/ticker/${ticker}`).then(r => r.data),
      api.get(`/api/smart-money/ticker/${ticker}`).then(r => r.data),
    ]).then(([sigResult, smResult]) => {
      // The signals endpoint can 200 with an {error} body (or a payload with
      // no net_sentiment) — treat both as the error state, otherwise the card
      // crashes on net_sentiment.toFixed / top_signals.length.
      if (sigResult.status === 'fulfilled') {
        const v = sigResult.value
        if (v == null || v.error != null || typeof v.net_sentiment !== 'number') {
          setError(true)
        } else {
          setSignals(v)
        }
      } else {
        setError(true)
      }
      if (smResult.status === 'fulfilled') setSmartMoney(smResult.value)
    }).finally(() => setLoading(false))
  }, [ticker])

  if (loading) {
    return (
      <div className="card p-4">
        <h3 className="text-sm font-semibold text-text-primary mb-3">Signal Intelligence</h3>
        <div className="space-y-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-8 rounded bg-surface-hover/40 animate-pulse" />
          ))}
        </div>
      </div>
    )
  }

  if (error || !signals) {
    return (
      <div className="card p-4">
        <h3 className="text-sm font-semibold text-text-primary mb-3">Signal Intelligence</h3>
        <p className="text-sm text-text-muted">No signal data available for {ticker.toUpperCase()}</p>
      </div>
    )
  }

  // get_ticker_intel emits strong_bull/bull/strong_bear/bear/neutral/no_data —
  // match those prefixes (the old 'bullish'/'bearish' checks never matched, so
  // the pill always rendered neutral zinc).
  const convergenceColor =
    (signals.convergence ?? '').includes('bull') ? 'bg-emerald-500/15 text-emerald-400'
    : (signals.convergence ?? '').includes('bear') ? 'bg-rose-500/15 text-rose-400'
    : 'bg-surface-hover text-text-secondary'

  const sentimentPct = Math.round(((signals.net_sentiment + 1) / 2) * 100) // -1..1 -> 0..100
  const sentimentColor = sentimentPct > 60 ? '#10b981' : sentimentPct < 40 ? '#f43f5e' : '#f59e0b'

  return (
    <div className="card p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Zap size={14} className="text-amber-400" />
          <h3 className="font-semibold text-text-primary text-sm">Signal Intelligence</h3>
        </div>
        <div className="flex items-center gap-2">
          {signals.conviction_quality != null && (
            <span
              title="Composite conviction quality (0-100): agreement x breadth x source spread, volume-adjusted"
              className={clsx('text-xs font-bold px-2 py-0.5 rounded-full',
                signals.conviction_quality.grade === 'A' ? 'bg-emerald-500/15 text-emerald-300'
                  : signals.conviction_quality.grade === 'B' ? 'bg-teal-500/15 text-teal-300'
                  : signals.conviction_quality.grade === 'C' ? 'bg-amber-500/15 text-amber-300'
                  : signals.conviction_quality.grade === 'D' ? 'bg-orange-500/15 text-orange-300'
                  : 'bg-rose-500/15 text-rose-300')}
            >
              {signals.conviction_quality.grade} {Math.round(signals.conviction_quality.score ?? 0)}
            </span>
          )}
          <span className={clsx('text-xs font-semibold px-2 py-0.5 rounded-full uppercase', convergenceColor)}>
            {/* Humanise backend tokens (no_data, strong_bull, ...) */}
            {(signals.convergence ?? 'no data').replace(/_/g, ' ')}
          </span>
        </div>
      </div>

      {/* Summary row */}
      <div className="flex items-center gap-3 text-xs text-text-muted flex-wrap">
        {/* Backend has no source_count field — derive from the sources list */}
        <span>
          {signals.signal_count ?? 0} signal{(signals.signal_count ?? 0) !== 1 ? 's' : ''} from{' '}
          {signals.sources?.length ?? 0} source{(signals.sources?.length ?? 0) !== 1 ? 's' : ''}
        </span>
        {signals.agreement_ratio != null && (signals.directional_count ?? 0) >= 2 && (
          <span
            title={`${Math.round(signals.agreement_ratio * 100)}% of ${signals.directional_count} directional signals agree — higher = cleaner conviction, ~50% = the crowd is split`}
            className={clsx('px-1.5 py-0.5 rounded font-medium',
              signals.agreement_ratio >= 0.8 ? 'bg-emerald-500/15 text-emerald-300'
                : signals.agreement_ratio >= 0.65 ? 'bg-amber-500/15 text-amber-300'
                : 'bg-rose-500/15 text-rose-300')}
          >
            {Math.round(signals.agreement_ratio * 100)}% agree
          </span>
        )}
      </div>

      {/* Net sentiment bar */}
      <div className="space-y-1">
        <div className="flex justify-between text-[11px] text-text-muted">
          <span>Bearish</span>
          <span className="font-mono font-semibold text-text-primary">
            {signals.net_sentiment >= 0 ? '+' : ''}{signals.net_sentiment.toFixed(2)}
          </span>
          <span>Bullish</span>
        </div>
        <div className="h-2 w-full rounded-full bg-surface-hover relative overflow-hidden">
          <div
            className="h-full rounded-full transition-all"
            style={{ width: `${sentimentPct}%`, backgroundColor: sentimentColor }}
          />
        </div>
      </div>

      {/* Smart money vs dumb money */}
      {smartMoney && (smartMoney.smart_money_score != null || smartMoney.dumb_money_score != null) && (
        <div className="grid grid-cols-2 gap-3">
          {smartMoney.smart_money_score != null && (
            <div className="bg-surface-hover/50 rounded-lg p-2 text-center">
              <div className="text-[11px] text-text-muted mb-0.5">Smart Money</div>
              <div className={clsx('text-sm font-bold font-mono', smartMoney.smart_money_score > 0 ? 'text-emerald-400' : smartMoney.smart_money_score < 0 ? 'text-rose-400' : 'text-text-muted')}>
                {smartMoney.smart_money_score > 0 ? '+' : ''}{smartMoney.smart_money_score.toFixed(2)}
              </div>
            </div>
          )}
          {smartMoney.dumb_money_score != null && (
            <div className="bg-surface-hover/50 rounded-lg p-2 text-center">
              <div className="text-[11px] text-text-muted mb-0.5">Retail</div>
              <div className={clsx('text-sm font-bold font-mono', smartMoney.dumb_money_score > 0 ? 'text-emerald-400' : smartMoney.dumb_money_score < 0 ? 'text-rose-400' : 'text-text-muted')}>
                {smartMoney.dumb_money_score > 0 ? '+' : ''}{smartMoney.dumb_money_score.toFixed(2)}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Top signals */}
      {(signals.top_signals ?? []).length > 0 && (
        <div className="space-y-2 pt-2 border-t border-border">
          <div className="text-[11px] text-text-muted uppercase tracking-wider">Top Signals</div>
          {signals.top_signals.slice(0, 3).map((sig, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className={clsx(
                'text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 mt-0.5',
                sig.sentiment > 0 ? 'bg-emerald-500/20 text-emerald-400' : sig.sentiment < 0 ? 'bg-rose-500/20 text-rose-400' : 'bg-surface-hover text-text-secondary',
              )}>
                {sig.source}
              </span>
              <p className="text-xs text-text-secondary leading-relaxed">{sig.summary}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function ForecastWidget({ ticker }: { ticker: string }) {
  const [f, setF] = useState<{
    has_forecast: boolean
    predicted_return_5d_pct?: number
    prob_up_pct?: number
    range_5d?: [number, number]
    verdict?: string
    backed_signals?: number
    backtested_signals?: number
    confidence?: 'high' | 'medium' | 'low'
    signal_count?: number
    calibration?: string
    reason?: string
  } | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true
    setLoading(true)
    api.get(`/api/predict/${encodeURIComponent(ticker)}`)
      .then(r => { if (alive) setF(r.data) })
      .catch(() => { if (alive) setF(null) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [ticker])

  if (loading) return null
  if (!f) return null

  if (!f.has_forecast) {
    return (
      <div className="card p-3 text-xs text-text-muted">
        <span className="font-semibold text-text-secondary">Forecast:</span>{' '}
        {f.reason === 'no_active_signals' ? 'No active signals on this ticker right now.'
          : 'Signals firing, but their sources need a backtest to calibrate a forecast.'}
      </div>
    )
  }

  const ret = f.predicted_return_5d_pct ?? 0
  const up = ret >= 0
  return (
    <div className="card p-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">Calibrated forecast · 5d</span>
        <div className="flex items-center gap-1.5">
          {f.confidence && (
            <span className={clsx('text-[9px] px-1.5 py-0.5 rounded font-medium',
              f.confidence === 'high' ? 'bg-emerald-500/15 text-emerald-300'
                : f.confidence === 'medium' ? 'bg-amber-500/15 text-amber-300'
                : 'bg-surface-hover text-text-muted')}>
              {f.confidence}
            </span>
          )}
          <span className="text-[10px] text-text-muted">
            {f.backtested_signals ?? 0} measured
            {(f.backed_signals ?? 0) > (f.backtested_signals ?? 0) ? ` · ${(f.backed_signals ?? 0) - (f.backtested_signals ?? 0)} prior` : ''}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-4 mt-1.5">
        <span className={clsx('text-2xl font-bold tabular-nums', up ? 'text-emerald-400' : 'text-rose-400')}>
          {up ? '+' : ''}{ret.toFixed(1)}%
        </span>
        <div className="text-xs text-text-muted">
          <div>P(up) <span className="text-text-secondary font-medium">{(f.prob_up_pct ?? 0).toFixed(0)}%</span></div>
          {f.range_5d && <div>range {f.range_5d[0].toFixed(1)}% to {f.range_5d[1].toFixed(1)}%</div>}
        </div>
      </div>
      {f.calibration && <p className="text-[10px] text-text-muted mt-2">{f.calibration}</p>}
    </div>
  )
}

function PhDSwarmWidget({ ticker }: { ticker: string }) {
  const [data, setData] = useState<{
    consensus?: { signal?: string; confidence?: number; bullish_pct?: number; bearish_pct?: number; neutral_pct?: number; agent_count?: number }
    desk_views?: Record<string, { signal?: string; confidence?: number }>
    agent_count?: number
  } | null>(null)
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [requested, setRequested] = useState(false)

  const load = async () => {
    if (requested) { setOpen(o => !o); return }
    setRequested(true); setOpen(true); setLoading(true)
    try {
      const r = await api.get(`/api/phd-swarm/consensus/${encodeURIComponent(ticker)}`)
      setData(r.data ?? null)
    } catch {
      setData(null)
    } finally {
      setLoading(false)
    }
  }

  const sigTone = (s?: string) =>
    s === 'bullish' ? 'text-emerald-400' : s === 'bearish' ? 'text-rose-400' : 'text-text-secondary'

  const c = data?.consensus
  const desks = Object.entries(data?.desk_views ?? {})

  return (
    <div className="card p-3">
      <button type="button" onClick={load} className="w-full flex items-center justify-between">
        <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">PhD Research Desk · 100 agents</span>
        {c?.signal ? (
          <span className={clsx('text-xs font-bold uppercase', sigTone(c.signal))}>
            {c.signal}{c.confidence != null ? ` · ${Math.round(c.confidence)}%` : ''}
          </span>
        ) : (
          <span className="text-[11px] text-blue-light">{open ? 'hide' : 'run the swarm →'}</span>
        )}
      </button>
      {open && (
        <div className="mt-2">
          {loading && <div className="text-[11px] text-text-muted">100 PhD agents across 8 desks are analysing {ticker}…</div>}
          {!loading && c && (
            <div className="space-y-2">
              {/* bullish/bearish split bar */}
              <div className="flex h-1.5 rounded-full overflow-hidden bg-surface-hover">
                <div className="bg-emerald-500/70" style={{ width: `${Math.max(0, Math.min(100, c.bullish_pct ?? 0))}%` }} />
                <div className="bg-border-light/40" style={{ width: `${Math.max(0, Math.min(100, c.neutral_pct ?? 0))}%` }} />
                <div className="bg-rose-500/60" style={{ width: `${Math.max(0, Math.min(100, c.bearish_pct ?? 0))}%` }} />
              </div>
              <div className="flex items-center gap-3 text-[10px] text-text-muted">
                <span className="text-emerald-400">{(c.bullish_pct ?? 0).toFixed(0)}% bull</span>
                <span>{(c.neutral_pct ?? 0).toFixed(0)}% neutral</span>
                <span className="text-rose-400">{(c.bearish_pct ?? 0).toFixed(0)}% bear</span>
                <span className="ml-auto">{c.agent_count ?? data?.agent_count ?? 0} agents</span>
              </div>
              {desks.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {desks.map(([desk, v]) => (
                    <span key={desk} className="text-[10px] px-1.5 py-0.5 rounded bg-surface-hover/60" title={`${desk}: ${v?.signal ?? 'neutral'}`}>
                      {desk} <span className={sigTone(v?.signal)}>{(v?.signal ?? 'neutral').slice(0, 4)}</span>
                    </span>
                  ))}
                </div>
              )}
              <Link to="/app/phd-swarm" className="inline-block text-[10px] text-blue-light hover:underline">
                full swarm →
              </Link>
            </div>
          )}
          {!loading && !c && requested && (
            <div className="text-[11px] text-text-muted">No swarm view available right now.</div>
          )}
        </div>
      )}
    </div>
  )
}

type PersonaTake = {
  agent_id?: string
  action?: string
  confidence?: number
  title?: string
  reasoning?: string
  data?: { persona_name?: string; persona_style?: string }
}

function PersonaConsensusWidget({ ticker }: { ticker: string }) {
  const [takes, setTakes] = useState<PersonaTake[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [requested, setRequested] = useState(false)

  // On-demand (LLM calls cost) — load when the user expands, not on mount.
  const load = async () => {
    if (requested) { setOpen(o => !o); return }
    setRequested(true); setOpen(true); setLoading(true)
    try {
      const r = await api.post(`/api/personas/compare/${encodeURIComponent(ticker)}`)
      setTakes(r.data?.personas ?? [])
    } catch {
      setTakes([])
    } finally {
      setLoading(false)
    }
  }

  const actionTone = (a?: string) =>
    a === 'BUY' ? 'text-emerald-400' : a === 'SELL' ? 'text-rose-400'
      : a === 'ALERT' ? 'text-amber-400' : 'text-text-secondary'

  // Consensus = majority action among non-INFO takes.
  const valid = (takes ?? []).filter(t => t.action && t.action !== 'INFO')
  const counts: Record<string, number> = {}
  for (const t of valid) counts[t.action as string] = (counts[t.action as string] ?? 0) + 1
  const consensus = Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0]
  const avgConf = valid.length
    ? Math.round(valid.reduce((s, t) => s + (t.confidence ?? 0), 0) / valid.length) : null

  return (
    <div className="card p-3">
      <button type="button" onClick={load} className="w-full flex items-center justify-between">
        <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">AI Persona Consensus</span>
        {consensus ? (
          <span className={clsx('text-xs font-bold', actionTone(consensus))}>
            {consensus}{avgConf != null ? ` · ${avgConf}%` : ''}
          </span>
        ) : (
          <span className="text-[11px] text-blue-light">{open ? 'hide' : 'ask the 6 investors →'}</span>
        )}
      </button>
      {open && (
        <div className="mt-2">
          {loading && <div className="text-[11px] text-text-muted">Buffett, Dalio, Soros, Simons, Wood & Burry are weighing in…</div>}
          {!loading && (takes ?? []).length > 0 && (
            <div className="space-y-1.5">
              {(takes ?? []).map((t, i) => (
                <div key={i} className="text-[11px]">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-text-primary">{t.data?.persona_name ?? t.agent_id ?? 'Investor'}</span>
                    <span className={clsx('font-bold', actionTone(t.action))}>{t.action}</span>
                    {t.confidence != null && <span className="text-text-muted">{Math.round(t.confidence)}%</span>}
                  </div>
                  {t.reasoning && <p className="text-text-muted leading-snug mt-0.5">{t.reasoning}</p>}
                </div>
              ))}
              <Link to="/app/round-table" className="inline-block text-[10px] text-blue-light hover:underline mt-1">
                full round-table →
              </Link>
            </div>
          )}
          {!loading && (takes ?? []).length === 0 && requested && (
            <div className="text-[11px] text-text-muted">No persona view available right now.</div>
          )}
        </div>
      )}
    </div>
  )
}

function SmcWidget({ ticker }: { ticker: string }) {
  const [smc, setSmc] = useState<{
    has_structure: boolean
    structure_trend?: string
    headline?: string
    events?: { type: string; dir: string; level: number }[]
    fair_value_gaps?: { dir: string; top: number; bottom: number; mitigated: boolean }[]
    liquidity_sweeps?: { dir: string; level: number }[]
  } | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true
    setLoading(true)
    api.get(`/api/charts/smc/${encodeURIComponent(ticker)}`)
      .then(r => { if (alive) setSmc(r.data) })
      .catch(() => { if (alive) setSmc(null) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [ticker])

  if (loading || !smc || !smc.has_structure) return null

  const trend = smc.structure_trend ?? 'neutral'
  const tone = trend === 'bullish' ? 'text-emerald-400' : trend === 'bearish' ? 'text-rose-400' : 'text-text-muted'
  const openFvg = (smc.fair_value_gaps ?? []).find(f => !f.mitigated)

  return (
    <div className="card p-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">Market Structure (SMC)</span>
        <span className={clsx('text-xs font-bold uppercase', tone)}>{trend}</span>
      </div>
      <p className="text-xs text-text-secondary mt-1.5">{smc.headline}</p>
      <div className="flex items-center gap-2 mt-2 flex-wrap text-[10px]">
        {(smc.events ?? []).slice(-2).map((e, i) => (
          <span key={i} className={clsx('px-1.5 py-0.5 rounded', e.dir === 'bullish' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-rose-500/15 text-rose-400')}>
            {e.type} {e.dir} @ {e.level}
          </span>
        ))}
        {openFvg && (
          <span className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">
            open {openFvg.dir} FVG {openFvg.bottom}–{openFvg.top}
          </span>
        )}
        {(smc.liquidity_sweeps ?? []).slice(0, 1).map((s, i) => (
          <span key={i} className="px-1.5 py-0.5 rounded bg-blue-500/15 text-blue-light">{s.dir} sweep @ {s.level}</span>
        ))}
      </div>
    </div>
  )
}

function TrendWidget({ ticker }: { ticker: string }) {
  const [t, setT] = useState<{
    has_trend: boolean
    trend_score?: number
    strength?: number
    direction?: string
    acceleration?: string
    headline?: string
    components?: Record<string, { score: number | null }>
  } | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let alive = true
    setLoading(true)
    api.get(`/api/trends/${encodeURIComponent(ticker)}`)
      .then(r => { if (alive) setT(r.data) })
      .catch(() => { if (alive) setT(null) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [ticker])

  if (loading || !t || !t.has_trend) return null

  const score = t.trend_score ?? 0
  const dir = t.direction ?? 'neutral'
  const tone = dir === 'up' ? 'text-emerald-400' : dir === 'down' ? 'text-rose-400' : 'text-text-muted'

  return (
    <div className="card p-3">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-text-secondary uppercase tracking-wider">Unified Trend</span>
        <span className={clsx('text-sm font-bold', tone)}>
          {dir === 'up' ? '▲' : dir === 'down' ? '▼' : '◆'} {score >= 0 ? '+' : ''}{score.toFixed(0)}
          {t.acceleration === 'building' && <span className="ml-1 text-[10px] text-emerald-300">building</span>}
          {t.acceleration === 'fading' && <span className="ml-1 text-[10px] text-amber-300">fading</span>}
        </span>
      </div>
      {/* signed strength bar */}
      <div className="mt-2 h-1.5 rounded-full bg-surface-hover relative overflow-hidden">
        <div className="absolute top-0 bottom-0 left-1/2 w-px bg-border-light" />
        <div className={clsx('absolute top-0 bottom-0', score >= 0 ? 'bg-emerald-500/60 left-1/2' : 'bg-rose-500/60 right-1/2')}
             style={{ width: `${Math.min(50, Math.abs(score) / 2)}%` }} />
      </div>
      <p className="text-xs text-text-secondary mt-1.5">{t.headline}</p>
      <div className="flex items-center gap-1.5 mt-2 flex-wrap text-[10px] text-text-muted">
        {(['structure', 'momentum', 'velocity', 'acceleration'] as const).map(dim => {
          const sc = t.components?.[dim]?.score
          if (sc == null) return null
          return (
            <span key={dim} className="px-1.5 py-0.5 rounded bg-surface-hover/60" title={`${dim}: ${sc.toFixed(0)}`}>
              {dim.slice(0, 3)} <span className={sc >= 0 ? 'text-emerald-400' : 'text-rose-400'}>{sc >= 0 ? '+' : ''}{sc.toFixed(0)}</span>
            </span>
          )
        })}
      </div>
    </div>
  )
}

function OverviewTab({ ticker, quote, ratings, peers }: {
  ticker: string
  quote: StockDetailQuote
  ratings: StockDetailRatings
  peers: string[]
}) {
  return (
    <div className="space-y-6 stagger-children">
      {/* Key stats grid */}
      <div>
        <h3 className="text-sm font-semibold text-text-muted uppercase tracking-wider mb-3">Key Statistics</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          <StatCell label="P/E Ratio" value={quote.pe != null ? quote.pe.toFixed(1) : 'N/A'} />
          <StatCell label="Market Cap" value={fmtCap(quote.market_cap)} />
          <StatCell label="Volume" value={fmtVol(quote.volume)} />
          <StatCell label="Beta" value={quote.beta != null ? quote.beta.toFixed(2) : 'N/A'} />
          <StatCell label="Div Yield" value={quote.div_yield != null ? `${quote.div_yield.toFixed(2)}%` : 'None'} />
          <StatCell
            label="52W Range"
            value={
              quote.high_52w != null && quote.low_52w != null
                ? `${fmtPrice(quote.low_52w, quote.currency)} – ${fmtPrice(quote.high_52w, quote.currency)}`
                : 'N/A'
            }
          />
        </div>
      </div>

      {/* Embedded price chart */}
      <MiniChart ticker={ticker} currency={quote.currency ?? 'USD'} />

      {/* Unified trend — structure + momentum + signal velocity + acceleration */}
      <TrendWidget ticker={ticker} />

      {/* AI persona consensus — 6 legendary investors' take (on-demand) */}
      <PersonaConsensusWidget ticker={ticker} />

      {/* PhD research-desk swarm — 100 specialist agents' weighted consensus */}
      <PhDSwarmWidget ticker={ticker} />

      {/* Calibrated forecast — active signals × historical base rates */}
      <ForecastWidget ticker={ticker} />

      {/* Smart Money Concepts structure — the 'Phantom Flow' read */}
      <SmcWidget ticker={ticker} />

      {/* 52-week range bar */}
      {quote.high_52w != null && quote.low_52w != null && quote.price != null && (
        <div className="card p-4">
          <div className="text-[11px] text-text-muted uppercase tracking-wider mb-2">52-Week Price Range</div>
          <div className="flex items-center gap-2 text-xs text-text-muted mb-1">
            <span>{fmtPrice(quote.low_52w, quote.currency)}</span>
            <div className="flex-1 h-2 rounded-full bg-surface-hover relative">
              <div
                className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-rose-500 to-emerald-500"
                style={{
                  width: `${Math.max(2, ((quote.price - quote.low_52w) / (quote.high_52w - quote.low_52w)) * 100)}%`,
                }}
              />
            </div>
            <span>{fmtPrice(quote.high_52w, quote.currency)}</span>
          </div>
          <div className="text-center text-[11px] text-text-muted">
            Current: <span className="text-text-primary font-semibold">{fmtPrice(quote.price, quote.currency)}</span>
            {' '}(
            {(((quote.price - quote.low_52w) / (quote.high_52w - quote.low_52w)) * 100).toFixed(0)}% of range
            )
          </div>
        </div>
      )}

      {/* Quant rating card */}
      {ratings.quant_rating != null && (
        <div className="card p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Star size={16} className="text-amber-400" />
              <h3 className="font-semibold text-text-primary">Quant Rating</h3>
            </div>
            <div className={clsx('text-2xl font-bold font-mono', ratingBg(ratings.quant_rating))}>
              {ratings.quant_rating.toFixed(2)}
              <span className="text-sm font-normal text-text-muted ml-1">/ 5.00</span>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 text-center">
            {([
              { key: 'valuation', label: 'Value', data: ratings.valuation },
              { key: 'growth', label: 'Growth', data: ratings.growth },
              { key: 'profitability', label: 'Profit', data: ratings.profitability },
              { key: 'momentum', label: 'Momentum', data: ratings.momentum },
              { key: 'eps_revisions', label: 'EPS Rev', data: ratings.eps_revisions },
            ] as Array<{ key: string; label: string; data: StockDetailFactorGrade | null }>).map(({ label, data }) => (
              <div key={label} className="space-y-1">
                <div className="text-[11px] text-text-muted">{label}</div>
                <GradeBadge grade={data?.grade} />
                {data && (
                  <div className="text-[9px] text-text-muted">{data.percentile.toFixed(0)}%ile</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Peer comparison */}
      {peers.length > 0 && (
        <div className="card p-4">
          <div className="flex items-center gap-2 mb-3">
            <Users size={14} className="text-blue-light" />
            <h3 className="font-semibold text-text-primary text-sm">Peer Comparison</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {peers.map(peer => (
              <TickerLink key={peer} ticker={peer} className="text-sm px-3 py-1.5 bg-surface-hover rounded-lg border border-border hover:border-blue/50 transition-colors" />
            ))}
          </div>
        </div>
      )}

      {/* ── Signal Intelligence ──────────────────────────────────── */}
      <SignalIntelligenceSection ticker={ticker} />

      {/* ── Edge Check — historic pattern backtest ───────────────── */}
      <EdgeCheckPanel ticker={ticker} />
    </div>
  )
}

// EdgeCheckPanel — pulls recent sources firing on the ticker and offers
// a one-click backtest against the aggregator's historical dataset. If
// the current source-mix has historically produced winners, the chip
// glows emerald — a concrete edge signal before pulling the trigger.
function EdgeCheckPanel({ ticker }: { ticker: string }) {
  const [sources, setSources] = useState<string[]>([])
  const [direction, setDirection] = useState<'LONG' | 'SHORT'>('LONG')
  const [loadedIntel, setLoadedIntel] = useState(false)
  const [bt, setBt] = useState<{ phase: 'idle' | 'loading' | 'loaded' | 'none' | 'error'; n?: number; wr?: number; avg?: number; best?: string; worst?: string }>({ phase: 'idle' })

  useEffect(() => {
    setLoadedIntel(false)
    api.get(`/api/signals/ticker/${encodeURIComponent(ticker)}`)
      .then(r => {
        const sigs = (r.data?.top_signals ?? []) as Array<{ source?: string }>
        const seen = new Set<string>()
        const uniq: string[] = []
        for (const s of sigs) {
          if (s.source && !seen.has(s.source)) {
            seen.add(s.source)
            uniq.push(s.source)
            if (uniq.length >= 6) break
          }
        }
        setSources(uniq)
        const netSent: number = r.data?.net_sentiment ?? 0
        setDirection(netSent >= 0 ? 'LONG' : 'SHORT')
      })
      .catch(() => setSources([]))
      .finally(() => setLoadedIntel(true))
  }, [ticker])

  const run = async () => {
    if (sources.length === 0) return
    setBt({ phase: 'loading' })
    try {
      const res = await api.get('/api/pattern-backtest', {
        params: {
          sources: sources.join(','),
          direction,
          days_ahead: 5,
          lookback_days: 90,
          min_source_match: Math.min(2, sources.length),
        },
      })
      const d = res.data
      if ((d?.n_samples ?? 0) === 0) {
        setBt({ phase: 'none' })
      } else {
        setBt({
          phase: 'loaded',
          n: d.n_samples,
          wr: d.win_rate_pct,
          avg: d.avg_return_pct,
          best: d.best_ticker,
          worst: d.worst_ticker,
        })
      }
    } catch {
      setBt({ phase: 'error' })
    }
  }

  if (!loadedIntel || sources.length === 0) return null

  const color = bt.phase === 'loaded'
    ? (bt.wr! >= 60 && (bt.avg ?? 0) > 0 ? 'text-emerald-300 border-emerald-500/40 bg-emerald-500/5'
      : bt.wr! <= 40 || (bt.avg ?? 0) < 0 ? 'text-rose-300 border-rose-500/40 bg-rose-500/5'
      : 'text-amber-300 border-amber-500/40 bg-amber-500/5')
    : 'text-text-secondary border-border bg-surface-card'

  return (
    <div className={clsx('rounded-lg border p-4', color)}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-[11px] uppercase tracking-wider opacity-80">📊 Edge Check</p>
          <p className="text-xs text-text-muted mt-1">
            {sources.length} live sources firing on {ticker} → run historical pattern-backtest ({direction}, 5d ahead, 90d lookback).
          </p>
        </div>
        {bt.phase === 'idle' && (
          <button
            type="button"
            onClick={run}
            className="text-xs px-3 py-1 rounded border border-blue-500/40 bg-blue-500/10 text-blue-300 hover:bg-blue-500/20 transition"
          >
            Run edge check →
          </button>
        )}
        {bt.phase === 'loading' && <span className="text-xs text-text-muted">Backtesting…</span>}
        {bt.phase === 'none' && <span className="text-xs text-text-muted">No historical pattern</span>}
        {bt.phase === 'error' && <span className="text-xs text-rose-400">Backtest failed</span>}
        {bt.phase === 'loaded' && (
          <div className="text-xs font-mono font-semibold">
            {bt.n}× · WR {bt.wr!.toFixed(0)}% · avg {(bt.avg ?? 0) >= 0 ? '+' : ''}{(bt.avg ?? 0).toFixed(2)}%
            {bt.best && <span className="ml-2 text-text-muted">(best {bt.best})</span>}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Technicals Tab ────────────────────────────────────────────────────────────

function TechnicalsTab({ technicals }: { technicals: import('@/lib/types').StockDetailTechnicals }) {
  return (
    <div className="space-y-6 stagger-children">
      {/* Overall trend */}
      <div className="card p-4 flex items-center justify-between">
        <div>
          <div className="text-[11px] text-text-muted uppercase tracking-wider mb-1">Overall Trend Signal</div>
          <TrendBadge trend={technicals.trend} />
        </div>
        <div className="text-right">
          <div className="text-[11px] text-text-muted uppercase tracking-wider mb-1">Current Price</div>
          <div className="font-mono text-lg font-semibold text-text-primary">
            {technicals.current_price != null ? `$${technicals.current_price.toFixed(2)}` : 'N/A'}
          </div>
        </div>
      </div>

      {/* RSI gauge */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-3">
          <Activity size={14} className="text-blue-light" />
          <h3 className="font-semibold text-text-primary text-sm">RSI (14)</h3>
        </div>
        <RSIGauge rsi={technicals.rsi} />
      </div>

      {/* MACD */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-3">
          <Zap size={14} className="text-blue-light" />
          <h3 className="font-semibold text-text-primary text-sm">MACD Signal</h3>
        </div>
        {technicals.macd_signal ? (
          <TrendBadge trend={technicals.macd_signal === 'NEUTRAL' ? 'NEUTRAL' : technicals.macd_signal} />
        ) : (
          <span className="text-text-muted text-sm">N/A</span>
        )}
        <p className="text-xs text-text-muted mt-2">
          {technicals.macd_signal === 'BULLISH'
            ? 'MACD line is above signal line — bullish momentum.'
            : technicals.macd_signal === 'BEARISH'
            ? 'MACD line is below signal line — bearish momentum.'
            : 'MACD and signal lines are close — no strong momentum.'}
        </p>
      </div>

      {/* SMAs */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-3">
          <BarChart2 size={14} className="text-blue-light" />
          <h3 className="font-semibold text-text-primary text-sm">Moving Averages</h3>
        </div>
        <div>
          <SMARow label="SMA 20" sma={technicals.sma_20} price={technicals.current_price} />
          <SMARow label="SMA 50" sma={technicals.sma_50} price={technicals.current_price} />
          <SMARow label="SMA 200" sma={technicals.sma_200} price={technicals.current_price} />
        </div>
      </div>

      {/* Bollinger Bands */}
      {(technicals.bb_upper != null || technicals.bb_lower != null) && (
        <div className="card p-4">
          <div className="flex items-center gap-2 mb-3">
            <Shield size={14} className="text-blue-light" />
            <h3 className="font-semibold text-text-primary text-sm">Bollinger Bands (20,2)</h3>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <div className="text-[11px] text-text-muted mb-1">Upper Band</div>
              <div className="font-mono text-sm text-rose-400">
                {technicals.bb_upper != null ? `$${technicals.bb_upper.toFixed(2)}` : 'N/A'}
              </div>
            </div>
            <div>
              <div className="text-[11px] text-text-muted mb-1">Price</div>
              <div className="font-mono text-sm text-text-primary">
                {technicals.current_price != null ? `$${technicals.current_price.toFixed(2)}` : 'N/A'}
              </div>
            </div>
            <div>
              <div className="text-[11px] text-text-muted mb-1">Lower Band</div>
              <div className="font-mono text-sm text-emerald-400">
                {technicals.bb_lower != null ? `$${technicals.bb_lower.toFixed(2)}` : 'N/A'}
              </div>
            </div>
          </div>
          {technicals.current_price != null && technicals.bb_upper != null && technicals.bb_lower != null && (
            <div className="mt-3">
              <div className="h-2 w-full rounded-full bg-surface-hover relative">
                <div
                  className="absolute inset-y-0 left-0 rounded-full bg-blue/60"
                  style={{
                    width: `${Math.max(0, Math.min(100,
                      ((technicals.current_price - technicals.bb_lower) /
                       (technicals.bb_upper - technicals.bb_lower)) * 100,
                    ))}%`,
                  }}
                />
              </div>
              <p className="text-center text-[11px] text-text-muted mt-1">
                {technicals.current_price > technicals.bb_upper
                  ? 'Price above upper band — potential overbought'
                  : technicals.current_price < technicals.bb_lower
                  ? 'Price below lower band — potential oversold'
                  : 'Price within bands'}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Fundamentals Tab ──────────────────────────────────────────────────────────

function FundamentalsTab({ profile, quote }: {
  profile: import('@/lib/types').StockDetailProfile
  quote: StockDetailQuote
}) {
  return (
    <div className="space-y-6 stagger-children">
      {/* Company profile */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-3">
          <Building2 size={14} className="text-blue-light" />
          <h3 className="font-semibold text-text-primary text-sm">Company Profile</h3>
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 mb-4">
          <div>
            <div className="text-[11px] text-text-muted uppercase tracking-wider">Sector</div>
            <div className="text-sm text-text-primary">{profile.sector || 'N/A'}</div>
          </div>
          <div>
            <div className="text-[11px] text-text-muted uppercase tracking-wider">Industry</div>
            <div className="text-sm text-text-primary">{profile.industry || 'N/A'}</div>
          </div>
          <div>
            <div className="text-[11px] text-text-muted uppercase tracking-wider">Country</div>
            <div className="text-sm text-text-primary">{profile.country || 'N/A'}</div>
          </div>
          <div>
            <div className="text-[11px] text-text-muted uppercase tracking-wider">Employees</div>
            <div className="text-sm text-text-primary">
              {profile.employees != null ? profile.employees.toLocaleString() : 'N/A'}
            </div>
          </div>
          {profile.website && (
            <div className="col-span-2">
              <div className="text-[11px] text-text-muted uppercase tracking-wider mb-0.5">Website</div>
              <a
                href={profile.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
              >
                <Globe size={12} />
                {profile.website.replace(/^https?:\/\//, '')}
                <ExternalLink size={10} />
              </a>
            </div>
          )}
        </div>
        {profile.description && (
          <div>
            <div className="text-[11px] text-text-muted uppercase tracking-wider mb-1">Description</div>
            <p className="text-sm text-text-secondary leading-relaxed line-clamp-6">
              {profile.description}
            </p>
          </div>
        )}
      </div>

      {/* Key financial metrics */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-3">
          <DollarSign size={14} className="text-blue-light" />
          <h3 className="font-semibold text-text-primary text-sm">Key Financial Metrics</h3>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <StatCell label="P/E Ratio" value={quote.pe != null ? quote.pe.toFixed(1) : 'N/A'} />
          <StatCell label="Market Cap" value={fmtCap(quote.market_cap)} />
          <StatCell label="Beta" value={quote.beta != null ? quote.beta.toFixed(2) : 'N/A'} />
          <StatCell label="Div Yield" value={quote.div_yield != null ? `${quote.div_yield.toFixed(2)}%` : 'None'} />
          <StatCell label="52W High" value={fmtPrice(quote.high_52w, quote.currency)} />
          <StatCell label="52W Low" value={fmtPrice(quote.low_52w, quote.currency)} />
        </div>
      </div>
    </div>
  )
}

// ── Earnings Tab ─────────────────────────────────────────────────────────────

function EarningsTab({ earnings }: { earnings: import('@/lib/types').StockDetailEarnings }) {
  return (
    <div className="space-y-6 stagger-children">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {/* Next earnings */}
        <div className="card p-4">
          <div className="text-[11px] text-text-muted uppercase tracking-wider mb-2">Next Earnings Date</div>
          <div className="text-lg font-semibold text-text-primary">
            {earnings.next_earnings_date ? fmtDate(earnings.next_earnings_date) : 'N/A'}
          </div>
        </div>

        {/* Beat rate */}
        <div className="card p-4">
          <div className="text-[11px] text-text-muted uppercase tracking-wider mb-2">Historical Beat Rate</div>
          <div className={clsx(
            'text-lg font-semibold font-mono',
            earnings.beat_rate != null && earnings.beat_rate >= 60 ? 'text-emerald-400' : 'text-amber-400',
          )}>
            {earnings.beat_rate != null ? `${earnings.beat_rate.toFixed(1)}%` : 'N/A'}
          </div>
          {earnings.beat_rate != null && (
            <div className="mt-2 h-1.5 w-full rounded-full bg-surface-hover">
              <div
                className={clsx(
                  'h-1.5 rounded-full',
                  earnings.beat_rate >= 60 ? 'bg-emerald-500' : 'bg-amber-500',
                )}
                style={{ width: `${earnings.beat_rate}%` }}
              />
            </div>
          )}
        </div>
      </div>

      {/* Most recent quarter */}
      {(earnings.last_eps != null || earnings.last_eps_estimate != null) && (
        <div className="card p-4">
          <h3 className="font-semibold text-text-primary text-sm mb-3">Most Recent Quarter</h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <div className="text-[11px] text-text-muted mb-1">EPS Estimate</div>
              <div className="font-mono text-sm text-text-secondary">
                {earnings.last_eps_estimate != null ? `$${earnings.last_eps_estimate.toFixed(2)}` : 'N/A'}
              </div>
            </div>
            <div>
              <div className="text-[11px] text-text-muted mb-1">EPS Actual</div>
              <div className="font-mono text-sm text-text-primary font-semibold">
                {earnings.last_eps != null ? `$${earnings.last_eps.toFixed(2)}` : 'N/A'}
              </div>
            </div>
            <div>
              <div className="text-[11px] text-text-muted mb-1">Surprise</div>
              <div className={clsx(
                'font-mono text-sm font-semibold',
                earnings.surprise_pct != null && earnings.surprise_pct >= 0 ? 'text-emerald-400' : 'text-rose-400',
              )}>
                {earnings.surprise_pct != null ? fmtPct(earnings.surprise_pct) : 'N/A'}
              </div>
            </div>
          </div>

          {/* Surprise bar */}
          {earnings.surprise_pct != null && (
            <div className="mt-4">
              <div className="flex items-center justify-center gap-2">
                <span className={clsx(
                  'text-sm font-semibold px-2 py-0.5 rounded',
                  earnings.surprise_pct >= 0
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : 'bg-rose-500/20 text-rose-400',
                )}>
                  {earnings.surprise_pct >= 0 ? 'Beat' : 'Missed'} by {Math.abs(earnings.surprise_pct).toFixed(1)}%
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Catalysts Tab ─────────────────────────────────────────────────────────────
// Aggregates FDA / halts / corp actions / restatements / earnings / short-
// squeeze / macro events for this specific ticker over the last 30 days.
function CatalystsTab({ ticker }: { ticker: string }) {
  const [data, setData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [decoded, setDecoded] = useState<any[]>([])
  const [thesis, setThesis] = useState<{ thesis: string; method: string } | null>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const [catRes, decRes, thesisRes] = await Promise.all([
          api.get(`/api/catalysts/ticker/${encodeURIComponent(ticker)}?hours=720`),
          api.get(`/api/winners/ticker/${encodeURIComponent(ticker)}?lookback_hours=168`),
          api.get(`/api/winners/thesis/${encodeURIComponent(ticker)}`),
        ])
        if (!cancelled) {
          setData(catRes.data)
          setDecoded(decRes.data?.decoded_bets ?? [])
          setThesis(thesisRes.data ? { thesis: thesisRes.data.thesis, method: thesisRes.data.method } : null)
        }
      } catch {
        if (!cancelled) { setData(null); setDecoded([]); setThesis(null) }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [ticker])

  if (loading) return <div className="text-sm text-text-muted">Loading catalysts…</div>

  const byCategory: Record<string, any[]> = data?.by_category ?? {}
  const total = data?.total ?? 0

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-text-primary">
          Catalysts for {ticker} — last 30 days ({total})
        </h3>
        <a href={`/app/catalysts?ticker=${encodeURIComponent(ticker)}`} className="text-xs text-blue-400 hover:underline">
          full page →
        </a>
      </div>

      {thesis?.thesis && (
        <div className={clsx(
          'rounded-xl border p-4 space-y-2',
          thesis.method === 'llm'
            ? 'bg-gradient-to-br from-blue-500/[0.08] to-amber-500/[0.05] border-blue-500/30'
            : 'bg-surface-card border-border',
        )}>
          <div className="flex items-center gap-2">
            <Brain size={14} className="text-blue-400" />
            <span className="text-[11px] uppercase text-text-muted tracking-wider">Thesis</span>
            <span className={clsx(
              'text-[9px] px-1.5 py-0.5 rounded border',
              thesis.method === 'llm'
                ? 'bg-blue-500/20 text-blue-300 border-blue-500/40'
                : 'bg-surface-hover text-text-secondary border-border',
            )}>
              {thesis.method === 'llm' ? 'DeepSeek' : 'template'}
            </span>
          </div>
          <p className="text-sm text-text-secondary leading-relaxed">{thesis.thesis}</p>
        </div>
      )}

      {total === 0 && decoded.length === 0 ? (
        <div className="text-center py-8 text-text-muted text-sm">
          No catalysts or decoded bets for {ticker} in the last 30 days.
        </div>
      ) : null}

      {decoded.length > 0 && (
        <div className="rounded-xl border border-blue-500/30 bg-blue-500/5 p-4 space-y-2">
          <div className="text-[11px] uppercase text-blue-300 font-semibold tracking-wider">
            Decoded smart-money bets (peak conviction{' '}
            {Math.max(...decoded.map((d: any) => d.inferred_conviction || 0))})
          </div>
          {decoded.slice(0, 8).map((b: any, i: number) => (
            <div key={i} className="flex items-start gap-3 text-xs">
              <span className={clsx(
                'shrink-0 text-[10px] px-1.5 py-0.5 rounded border font-bold',
                b.inferred_conviction >= 80 ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' :
                b.inferred_conviction >= 60 ? 'bg-blue-500/20 text-blue-300 border-blue-500/40' :
                'bg-surface-hover text-text-secondary border-border',
              )}>
                {b.inferred_conviction}
              </span>
              <span className="text-text-muted shrink-0">{b.source}</span>
              <span className="text-text-secondary">{b.thesis}</span>
            </div>
          ))}
        </div>
      )}

      {Object.entries(byCategory).map(([cat, items]) => (
        <div key={cat} className="space-y-2">
          <h4 className="text-xs font-semibold text-text-muted uppercase tracking-wider">
            {cat} <span className="text-text-muted">({items.length})</span>
          </h4>
          <div className="space-y-1">
            {items.slice(0, 10).map((c: any, i: number) => (
              <div key={i} className={clsx(
                'rounded-lg border px-3 py-2 text-xs flex items-start justify-between gap-3',
                c.sentiment >= 0.3 ? 'bg-emerald-500/5 border-emerald-500/20' :
                c.sentiment <= -0.3 ? 'bg-rose-500/5 border-rose-500/20' :
                'bg-surface-card border-border',
              )}>
                <span className="text-text-secondary">{c.summary}</span>
                <span className="text-text-muted shrink-0">{c.source}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

// ── Alt Data Tab ──────────────────────────────────────────────────────────────

function AltDataTab({ data }: { data: import('@/lib/types').StockDetail }) {
  const { insider, congress, short_interest, social, institutional, options } = data

  return (
    <div className="space-y-6 stagger-children">
      {/* Insider trades */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Eye size={14} className="text-blue-light" />
            <h3 className="font-semibold text-text-primary text-sm">Insider Trades</h3>
          </div>
          <TrendBadge trend={insider.net_sentiment} />
        </div>
        {insider.recent_trades.length === 0 ? (
          <p className="text-text-muted text-sm">No recent insider trades.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-text-muted border-b border-border">
                  <th className="text-left py-1.5 pr-3">Name</th>
                  <th className="text-left py-1.5 pr-3">Title</th>
                  <th className="text-left py-1.5 pr-3">Type</th>
                  <th className="text-right py-1.5 pr-3">Shares</th>
                  <th className="text-right py-1.5 pr-3">Value</th>
                  <th className="text-right py-1.5">Date</th>
                </tr>
              </thead>
              <tbody>
                {insider.recent_trades.map((tr, i) => (
                  <tr key={i} className="border-b border-border/50 hover:bg-surface-hover/30">
                    <td className="py-1.5 pr-3 text-text-primary">{tr.name}</td>
                    <td className="py-1.5 pr-3 text-text-muted">{tr.title || '-'}</td>
                    <td className={clsx(
                      'py-1.5 pr-3 font-semibold',
                      tr.transaction_type.toLowerCase().includes('buy') || tr.transaction_type.toLowerCase().includes('purchase')
                        ? 'text-emerald-400' : 'text-rose-400',
                    )}>
                      {tr.transaction_type}
                    </td>
                    <td className="py-1.5 pr-3 text-right font-mono text-text-secondary">
                      {tr.shares != null ? tr.shares.toLocaleString() : '-'}
                    </td>
                    <td className="py-1.5 pr-3 text-right font-mono text-text-secondary">
                      {tr.value != null ? fmtCap(tr.value) : '-'}
                    </td>
                    <td className="py-1.5 text-right text-text-muted">{fmtDate(tr.date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Congressional trades */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Building2 size={14} className="text-blue-light" />
            <h3 className="font-semibold text-text-primary text-sm">Congressional Trades</h3>
          </div>
          <TrendBadge trend={congress.net_sentiment} />
        </div>
        {congress.recent_trades.length === 0 ? (
          <p className="text-text-muted text-sm">No recent congressional trades.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-text-muted border-b border-border">
                  <th className="text-left py-1.5 pr-3">Member</th>
                  <th className="text-left py-1.5 pr-3">Chamber</th>
                  <th className="text-left py-1.5 pr-3">Type</th>
                  <th className="text-right py-1.5 pr-3">Amount</th>
                  <th className="text-right py-1.5">Date</th>
                </tr>
              </thead>
              <tbody>
                {congress.recent_trades.map((tr, i) => (
                  <tr key={i} className="border-b border-border/50 hover:bg-surface-hover/30">
                    <td className="py-1.5 pr-3 text-text-primary">{tr.member_name}</td>
                    <td className="py-1.5 pr-3 text-text-muted">{tr.chamber || '-'}</td>
                    <td className={clsx(
                      'py-1.5 pr-3 font-semibold',
                      tr.transaction_type.toLowerCase().includes('purchase') || tr.transaction_type.toLowerCase().includes('buy')
                        ? 'text-emerald-400' : 'text-rose-400',
                    )}>
                      {tr.transaction_type}
                    </td>
                    <td className="py-1.5 pr-3 text-right font-mono text-text-secondary">{tr.amount}</td>
                    <td className="py-1.5 text-right text-text-muted">{fmtDate(tr.transaction_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Short interest + social + options */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card p-4">
          <div className="text-[11px] text-text-muted uppercase tracking-wider mb-2">Short Interest</div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-xs text-text-muted">Short %</span>
              <span className="font-mono text-sm text-text-primary">
                {short_interest.short_pct != null ? `${short_interest.short_pct.toFixed(1)}%` : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-text-muted">Days to Cover</span>
              <span className="font-mono text-sm text-text-primary">
                {short_interest.days_to_cover != null ? short_interest.days_to_cover.toFixed(1) : 'N/A'}
              </span>
            </div>
          </div>
        </div>

        <div className="card p-4">
          <div className="text-[11px] text-text-muted uppercase tracking-wider mb-2">Social Sentiment</div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-xs text-text-muted">Reddit Mentions</span>
              <span className="font-mono text-sm text-text-primary">
                {social.reddit_mentions != null ? social.reddit_mentions.toLocaleString() : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-text-muted">Fear/Greed</span>
              <span className={clsx(
                'font-mono text-sm font-semibold',
                social.fear_greed != null
                  ? social.fear_greed >= 60 ? 'text-emerald-400'
                  : social.fear_greed <= 30 ? 'text-rose-400'
                  : 'text-amber-400'
                  : 'text-text-muted',
              )}>
                {social.fear_greed != null ? social.fear_greed : 'N/A'}
              </span>
            </div>
          </div>
        </div>

        <div className="card p-4">
          <div className="text-[11px] text-text-muted uppercase tracking-wider mb-2">Options</div>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-xs text-text-muted">Put/Call Ratio</span>
              <span className={clsx(
                'font-mono text-sm font-semibold',
                options.put_call_ratio != null
                  ? options.put_call_ratio > 1.0 ? 'text-rose-400' : 'text-emerald-400'
                  : 'text-text-muted',
              )}>
                {options.put_call_ratio != null ? options.put_call_ratio.toFixed(2) : 'N/A'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-xs text-text-muted">IV Rank</span>
              <span className={clsx(
                'font-mono text-sm font-semibold',
                options.iv_rank != null
                  ? options.iv_rank >= 70 ? 'text-rose-400'
                  : options.iv_rank <= 30 ? 'text-emerald-400'
                  : 'text-amber-400'
                  : 'text-text-muted',
              )}>
                {options.iv_rank != null ? `${options.iv_rank.toFixed(0)}%` : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Institutional holders */}
      {institutional.top_holders.length > 0 && (
        <div className="card p-4">
          <div className="flex items-center gap-2 mb-3">
            <Users size={14} className="text-blue-light" />
            <h3 className="font-semibold text-text-primary text-sm">Top Institutional Holders</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-text-muted border-b border-border">
                  <th className="text-left py-1.5 pr-3">Institution</th>
                  <th className="text-right py-1.5 pr-3">Shares</th>
                  <th className="text-right py-1.5 pr-3">Value</th>
                  <th className="text-right py-1.5">% Held</th>
                </tr>
              </thead>
              <tbody>
                {institutional.top_holders.map((h, i) => (
                  <tr key={i} className="border-b border-border/50 hover:bg-surface-hover/30">
                    <td className="py-1.5 pr-3 text-text-primary">{h.name}</td>
                    <td className="py-1.5 pr-3 text-right font-mono text-text-secondary">
                      {h.shares.toLocaleString()}
                    </td>
                    <td className="py-1.5 pr-3 text-right font-mono text-text-secondary">
                      {h.value != null ? fmtCap(h.value) : '-'}
                    </td>
                    <td className="py-1.5 text-right font-mono text-text-secondary">
                      {h.pct_held != null ? `${(h.pct_held * 100).toFixed(2)}%` : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Financials Tab ────────────────────────────────────────────────────────────

function fmtBn(v: number | null | undefined): string {
  if (v == null) return 'N/A'
  const abs = Math.abs(v)
  const sign = v < 0 ? '-' : ''
  if (abs >= 1e12) return `${sign}$${(abs / 1e12).toFixed(2)}T`
  if (abs >= 1e9)  return `${sign}$${(abs / 1e9).toFixed(2)}B`
  if (abs >= 1e6)  return `${sign}$${(abs / 1e6).toFixed(2)}M`
  return `${sign}$${abs.toLocaleString()}`
}

function fmtRatio(v: number | null | undefined, suffix = 'x'): string {
  if (v == null) return 'N/A'
  return `${v.toFixed(2)}${suffix}`
}

function fmtMargin(v: number | null | undefined): string {
  if (v == null) return 'N/A'
  return `${v.toFixed(1)}%`
}

function marginColor(v: number | null | undefined): string {
  if (v == null) return 'text-text-muted'
  if (v >= 20) return 'text-emerald-400'
  if (v >= 10) return 'text-sky-400'
  if (v >= 0)  return 'text-amber-400'
  return 'text-rose-400'
}

function cfColor(v: number | null | undefined): string {
  if (v == null) return 'text-text-muted'
  return v >= 0 ? 'text-emerald-400' : 'text-rose-400'
}

function FinancialsTab({ ticker, financials, loading, error }: {
  ticker: string
  financials: StockFinancials | null
  loading: boolean
  error: string | null
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center gap-2 py-16 text-text-muted">
        <Loader2 size={20} className="animate-spin" />
        <span className="text-sm">Loading financial statements…</span>
      </div>
    )
  }

  if (error || !financials) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 py-16 text-text-muted">
        <AlertTriangle size={20} className="text-amber-400" />
        <span className="text-sm">{error ?? 'Financial data unavailable.'}</span>
      </div>
    )
  }

  const { income_statements: income, balance_sheets: balance, cash_flows: cashflows, ratios } = financials

  return (
    <div className="space-y-6 stagger-children">
      {/* Income Statement */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-3">
          <FileText size={14} className="text-blue-light" />
          <h3 className="font-semibold text-text-primary text-sm">Income Statement</h3>
          {financials.data_source && (
            <span className="ml-auto text-[10px] text-text-muted">Source: {financials.data_source}</span>
          )}
        </div>
        {income.length === 0 ? (
          <p className="text-text-muted text-sm">No income statement data available.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-text-muted border-b border-border">
                  <th className="text-left py-1.5 pr-4">Year</th>
                  <th className="text-right py-1.5 pr-4">Revenue</th>
                  <th className="text-right py-1.5 pr-4">Gross Profit</th>
                  <th className="text-right py-1.5 pr-4">Op. Income</th>
                  <th className="text-right py-1.5 pr-4">Net Income</th>
                  <th className="text-right py-1.5">EBITDA</th>
                </tr>
              </thead>
              <tbody>
                {income.map((row, i) => {
                  const yr = row.fiscal_year ?? row.date?.slice(0, 4) ?? '-'
                  return (
                    <tr key={i} className="border-b border-border/50 hover:bg-surface-hover/30">
                      <td className="py-2 pr-4 font-semibold text-text-primary">{yr}</td>
                      <td className="py-2 pr-4 text-right font-mono text-text-secondary">{fmtBn(row.revenue)}</td>
                      <td className={clsx('py-2 pr-4 text-right font-mono', cfColor(row.gross_profit))}>{fmtBn(row.gross_profit)}</td>
                      <td className={clsx('py-2 pr-4 text-right font-mono', cfColor(row.operating_income))}>{fmtBn(row.operating_income)}</td>
                      <td className={clsx('py-2 pr-4 text-right font-mono', cfColor(row.net_income))}>{fmtBn(row.net_income)}</td>
                      <td className={clsx('py-2 text-right font-mono', cfColor(row.ebitda))}>{fmtBn(row.ebitda)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Balance Sheet */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-3">
          <Building2 size={14} className="text-blue-light" />
          <h3 className="font-semibold text-text-primary text-sm">Balance Sheet</h3>
        </div>
        {balance.length === 0 ? (
          <p className="text-text-muted text-sm">No balance sheet data available.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-text-muted border-b border-border">
                  <th className="text-left py-1.5 pr-4">Year</th>
                  <th className="text-right py-1.5 pr-4">Total Assets</th>
                  <th className="text-right py-1.5 pr-4">Total Liab.</th>
                  <th className="text-right py-1.5 pr-4">Equity</th>
                  <th className="text-right py-1.5 pr-4">Cash</th>
                  <th className="text-right py-1.5">LT Debt</th>
                </tr>
              </thead>
              <tbody>
                {balance.map((row, i) => {
                  const yr = row.fiscal_year ?? row.date?.slice(0, 4) ?? '-'
                  return (
                    <tr key={i} className="border-b border-border/50 hover:bg-surface-hover/30">
                      <td className="py-2 pr-4 font-semibold text-text-primary">{yr}</td>
                      <td className="py-2 pr-4 text-right font-mono text-text-secondary">{fmtBn(row.total_assets)}</td>
                      <td className="py-2 pr-4 text-right font-mono text-rose-400/80">{fmtBn(row.total_liabilities)}</td>
                      <td className={clsx('py-2 pr-4 text-right font-mono', cfColor(row.total_equity))}>{fmtBn(row.total_equity)}</td>
                      <td className="py-2 pr-4 text-right font-mono text-emerald-400/80">{fmtBn(row.cash)}</td>
                      <td className="py-2 text-right font-mono text-text-muted">{fmtBn(row.long_term_debt)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Cash Flow */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-3">
          <DollarSign size={14} className="text-blue-light" />
          <h3 className="font-semibold text-text-primary text-sm">Cash Flow Statement</h3>
        </div>
        {cashflows.length === 0 ? (
          <p className="text-text-muted text-sm">No cash flow data available.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-text-muted border-b border-border">
                  <th className="text-left py-1.5 pr-4">Year</th>
                  <th className="text-right py-1.5 pr-4">Operating CF</th>
                  <th className="text-right py-1.5 pr-4">Investing CF</th>
                  <th className="text-right py-1.5 pr-4">Financing CF</th>
                  <th className="text-right py-1.5 pr-4">Free CF</th>
                  <th className="text-right py-1.5">CapEx</th>
                </tr>
              </thead>
              <tbody>
                {cashflows.map((row, i) => {
                  const yr = row.fiscal_year ?? row.date?.slice(0, 4) ?? '-'
                  return (
                    <tr key={i} className="border-b border-border/50 hover:bg-surface-hover/30">
                      <td className="py-2 pr-4 font-semibold text-text-primary">{yr}</td>
                      <td className={clsx('py-2 pr-4 text-right font-mono', cfColor(row.operating_cash_flow))}>{fmtBn(row.operating_cash_flow)}</td>
                      <td className={clsx('py-2 pr-4 text-right font-mono', cfColor(row.investing_cash_flow))}>{fmtBn(row.investing_cash_flow)}</td>
                      <td className={clsx('py-2 pr-4 text-right font-mono', cfColor(row.financing_cash_flow))}>{fmtBn(row.financing_cash_flow)}</td>
                      <td className={clsx('py-2 pr-4 text-right font-mono font-semibold', cfColor(row.free_cash_flow))}>{fmtBn(row.free_cash_flow)}</td>
                      <td className="py-2 text-right font-mono text-text-muted">{fmtBn(row.capex)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Key Ratios Grid */}
      <div className="card p-4">
        <div className="flex items-center gap-2 mb-4">
          <Zap size={14} className="text-blue-light" />
          <h3 className="font-semibold text-text-primary text-sm">Key Financial Ratios</h3>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {/* Valuation */}
          <div className="bg-surface-hover/30 rounded-lg p-3">
            <div className="text-[11px] text-text-muted uppercase tracking-wider mb-1">P/E Ratio</div>
            <div className="font-mono text-sm font-semibold text-text-primary">{fmtRatio(ratios.pe_ratio)}</div>
          </div>
          <div className="bg-surface-hover/30 rounded-lg p-3">
            <div className="text-[11px] text-text-muted uppercase tracking-wider mb-1">P/B Ratio</div>
            <div className="font-mono text-sm font-semibold text-text-primary">{fmtRatio(ratios.pb_ratio)}</div>
          </div>
          <div className="bg-surface-hover/30 rounded-lg p-3">
            <div className="text-[11px] text-text-muted uppercase tracking-wider mb-1">P/S Ratio</div>
            <div className="font-mono text-sm font-semibold text-text-primary">{fmtRatio(ratios.ps_ratio)}</div>
          </div>
          <div className="bg-surface-hover/30 rounded-lg p-3">
            <div className="text-[11px] text-text-muted uppercase tracking-wider mb-1">EV/EBITDA</div>
            <div className="font-mono text-sm font-semibold text-text-primary">{fmtRatio(ratios.ev_ebitda)}</div>
          </div>
          {/* Margins */}
          <div className="bg-surface-hover/30 rounded-lg p-3">
            <div className="text-[11px] text-text-muted uppercase tracking-wider mb-1">Gross Margin</div>
            <div className={clsx('font-mono text-sm font-semibold', marginColor(ratios.gross_margin))}>{fmtMargin(ratios.gross_margin)}</div>
          </div>
          <div className="bg-surface-hover/30 rounded-lg p-3">
            <div className="text-[11px] text-text-muted uppercase tracking-wider mb-1">Op. Margin</div>
            <div className={clsx('font-mono text-sm font-semibold', marginColor(ratios.operating_margin))}>{fmtMargin(ratios.operating_margin)}</div>
          </div>
          <div className="bg-surface-hover/30 rounded-lg p-3">
            <div className="text-[11px] text-text-muted uppercase tracking-wider mb-1">Net Margin</div>
            <div className={clsx('font-mono text-sm font-semibold', marginColor(ratios.net_margin))}>{fmtMargin(ratios.net_margin)}</div>
          </div>
          {/* Returns */}
          <div className="bg-surface-hover/30 rounded-lg p-3">
            <div className="text-[11px] text-text-muted uppercase tracking-wider mb-1">ROE</div>
            <div className={clsx('font-mono text-sm font-semibold', marginColor(ratios.roe))}>{fmtMargin(ratios.roe)}</div>
          </div>
          <div className="bg-surface-hover/30 rounded-lg p-3">
            <div className="text-[11px] text-text-muted uppercase tracking-wider mb-1">ROA</div>
            <div className={clsx('font-mono text-sm font-semibold', marginColor(ratios.roa))}>{fmtMargin(ratios.roa)}</div>
          </div>
          {/* Leverage */}
          <div className="bg-surface-hover/30 rounded-lg p-3">
            <div className="text-[11px] text-text-muted uppercase tracking-wider mb-1">Debt / Equity</div>
            <div className={clsx(
              'font-mono text-sm font-semibold',
              ratios.debt_to_equity == null ? 'text-text-muted'
              : ratios.debt_to_equity > 2 ? 'text-rose-400'
              : ratios.debt_to_equity > 1 ? 'text-amber-400'
              : 'text-emerald-400',
            )}>
              {fmtRatio(ratios.debt_to_equity, '')}
            </div>
          </div>
          <div className="bg-surface-hover/30 rounded-lg p-3">
            <div className="text-[11px] text-text-muted uppercase tracking-wider mb-1">Current Ratio</div>
            <div className={clsx(
              'font-mono text-sm font-semibold',
              ratios.current_ratio == null ? 'text-text-muted'
              : ratios.current_ratio >= 2 ? 'text-emerald-400'
              : ratios.current_ratio >= 1 ? 'text-amber-400'
              : 'text-rose-400',
            )}>
              {fmtRatio(ratios.current_ratio, '')}
            </div>
          </div>
          <div className="bg-surface-hover/30 rounded-lg p-3">
            <div className="text-[11px] text-text-muted uppercase tracking-wider mb-1">Quick Ratio</div>
            <div className="font-mono text-sm font-semibold text-text-primary">{fmtRatio(ratios.quick_ratio, '')}</div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Active Idea Banner ────────────────────────────────────────────────────
// Highlights live blotter entries for this ticker at the top of the stock
// detail page, so Simon sees the kept idea context without flipping to
// the blotter.
interface ActiveIdea {
  id: number
  direction: 'LONG' | 'SHORT' | 'WATCH'
  status: 'new' | 'saved' | 'acted'
  conviction?: number | null
  entry_price?: number | null
  stop_price?: number | null
  target_price?: number | null
  acted_price?: number | null
  acted_quantity?: number | null
  current_price?: number | null
  pnl_vs_entry_pct?: number | null
  notes?: string | null
}

function ActiveIdeaBanner({ ticker }: { ticker: string }) {
  const [ideas, setIdeas] = useState<ActiveIdea[]>([])
  const [loaded, setLoaded] = useState(false)

  const load = useCallback(() => {
    api.get(`/api/blotter/ideas?limit=10&ticker=${encodeURIComponent(ticker)}`)
      .then(r => {
        const all: ActiveIdea[] = r.data?.ideas ?? []
        setIdeas(all.filter(i => i.status === 'new' || i.status === 'saved' || i.status === 'acted'))
      })
      .catch(() => setIdeas([]))
      .finally(() => setLoaded(true))
  }, [ticker])

  useEffect(() => {
    setLoaded(false)
    load()
  }, [load])

  const quickKeep = async () => {
    const dir = window.prompt(`Keep ${ticker} as: LONG or SHORT?`, 'LONG')
    if (!dir) return
    const d = dir.trim().toUpperCase()
    if (d !== 'LONG' && d !== 'SHORT') return
    try {
      await api.post('/api/blotter/ideas', {
        ticker,
        direction: d,
        rationale: 'Manually kept from stock detail page',
      })
      load()
    } catch { /* silent */ }
  }

  if (!loaded) return null

  if (ideas.length === 0) {
    return (
      <div className="mt-3 rounded-lg border border-border bg-surface-card/40 px-3 py-2 flex items-center justify-between gap-3 text-xs">
        <span className="text-text-muted">No saved idea for {ticker}.</span>
        <button
          type="button"
          onClick={quickKeep}
          className="text-[10px] px-2 py-1 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"
          title="Create a saved blotter entry for this ticker"
        >
          + Keep this idea
        </button>
      </div>
    )
  }

  return (
    <div className="mt-3 space-y-1.5">
      {ideas.map(i => {
        const bg = i.status === 'acted' ? 'border-blue-500/40 bg-blue-500/10'
          : i.direction === 'LONG' ? 'border-emerald-500/40 bg-emerald-500/10'
          : i.direction === 'SHORT' ? 'border-rose-500/40 bg-rose-500/10'
          : 'border-border bg-surface-hover/60'
        return (
          <div key={i.id} className={clsx('rounded-lg border px-3 py-2 text-xs flex items-center gap-3 flex-wrap', bg)}>
            <span className="text-[11px] uppercase tracking-wider font-bold">
              {i.status === 'acted' ? '✈ In Flight' : i.status === 'saved' ? '🎯 Saved Idea' : '• New Idea'}
            </span>
            <span className="font-mono font-bold">{i.direction}</span>
            {i.conviction != null && <span>Conv {Math.round(i.conviction)}</span>}
            {i.entry_price != null && (
              <span className="text-text-secondary">
                Entry <span className="font-mono">{i.entry_price.toFixed(2)}</span>
              </span>
            )}
            {i.stop_price != null && (
              <span className="text-rose-300">
                Stop <span className="font-mono">{i.stop_price.toFixed(2)}</span>
              </span>
            )}
            {i.target_price != null && (
              <span className="text-emerald-300">
                Target <span className="font-mono">{i.target_price.toFixed(2)}</span>
              </span>
            )}
            {i.acted_price != null && i.current_price != null && (
              <span className={clsx(
                'font-mono font-semibold ml-auto',
                (i.current_price - i.acted_price) * (i.direction === 'LONG' ? 1 : -1) >= 0
                  ? 'text-emerald-400' : 'text-rose-400',
              )}>
                {(((i.current_price - i.acted_price) / i.acted_price * 100) * (i.direction === 'LONG' ? 1 : -1)).toFixed(2)}%
              </span>
            )}
            <Link
              to="/app/blotter"
              className="text-[10px] underline decoration-dotted text-blue-light hover:opacity-80 ml-auto"
            >
              Open blotter →
            </Link>
          </div>
        )
      })}
    </div>
  )
}

// ── Signals History Tab ────────────────────────────────────────────────────────

interface HistorySignal {
  source: string
  signal_type: string
  sentiment: number | null
  confidence: number | null
  score: number | null
  summary: string | null
  detail: string | null
  actor: string | null
  actor_role: string | null
  raw_url: string | null
  tags: string[]
  created_at: string | null
}

interface HistoryTimelineEntry {
  date: string
  count: number
  bull: number
  bear: number
  avg_sent: number
}

interface SignalMomentum {
  direction: 'accelerating' | 'fading' | 'steady'
  volume_change_pct: number
  sentiment_shift: number
  vol_recent: number
  vol_older: number
  note: string
}

interface SimilarTicker {
  ticker: string
  matched_sources: number
  sources: string[]
  avg_sentiment: number
  direction: 'LONG' | 'SHORT'
}

function SimilarTickersPanel({ ticker }: { ticker: string }) {
  const [list, setList] = useState<SimilarTicker[]>([])
  const [loading, setLoading] = useState(true)
  const [unavailable, setUnavailable] = useState(false)
  const [retryKey, setRetryKey] = useState(0)

  useEffect(() => {
    setLoading(true)
    setUnavailable(false)
    api.get(`/api/signals/ticker/${encodeURIComponent(ticker)}/similar?lookback_hours=48&limit=8`)
      .then(r => {
        if (r.data?.error) {
          setUnavailable(true)
          setList([])
        } else {
          setList(r.data?.similar ?? [])
        }
      })
      .catch(() => { setList([]); setUnavailable(true) })
      .finally(() => setLoading(false))
  }, [ticker, retryKey])

  if (loading) return <div className="card p-4 h-24 animate-pulse bg-surface-hover/20" />

  if (unavailable) {
    return (
      <div className="card p-4 flex items-center justify-between gap-2">
        <p className="text-xs text-text-muted">Similar setups temporarily unavailable.</p>
        <button
          type="button"
          onClick={() => setRetryKey(k => k + 1)}
          className="text-xs text-blue-light hover:underline shrink-0"
        >
          Retry
        </button>
      </div>
    )
  }

  if (list.length === 0) return null

  return (
    <div className="card p-4">
      <h3 className="text-sm font-semibold text-text-primary mb-2">
        Similar Setups ({list.length})
      </h3>
      <p className="text-[11px] text-text-muted mb-2">
        Other tradeable tickers where 50%+ of this ticker's source mix is firing in the same direction over last 48h.
      </p>
      <div className="flex flex-wrap gap-2">
        {list.map(e => (
          <Link
            key={e.ticker}
            to={`/app/stock/${encodeURIComponent(e.ticker)}?tab=Signals`}
            className={clsx(
              'flex items-center gap-1.5 px-2 py-1 rounded border font-mono text-xs transition hover:opacity-80',
              e.direction === 'LONG'
                ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                : 'border-rose-500/40 bg-rose-500/10 text-rose-300',
            )}
            title={`${e.matched_sources ?? 0} sources match · avg sent ${(e.avg_sentiment ?? 0).toFixed(2)}`}
          >
            {e.ticker}
            <span className="text-[9px] opacity-80">{e.matched_sources ?? 0}src</span>
          </Link>
        ))}
      </div>
    </div>
  )
}

function SignalsHistoryTab({ ticker }: { ticker: string }) {
  const [signals, setSignals] = useState<HistorySignal[]>([])
  const [timeline, setTimeline] = useState<HistoryTimelineEntry[]>([])
  const [momentum, setMomentum] = useState<SignalMomentum | null>(null)
  const [days, setDays] = useState(30)
  const [loading, setLoading] = useState(true)
  const [sourceFilter, setSourceFilter] = useState<string | null>(null)
  const [unavailable, setUnavailable] = useState(false)
  const [retryKey, setRetryKey] = useState(0)

  useEffect(() => {
    setLoading(true)
    setUnavailable(false)
    api.get(`/api/signals/ticker/${encodeURIComponent(ticker)}/history?days=${days}`)
      .then(r => {
        setSignals(r.data?.signals ?? [])
        setTimeline(r.data?.timeline ?? [])
        setMomentum(r.data?.momentum ?? null)
        // A backend-caught failure still returns HTTP 200 with an 'error'
        // field + empty arrays (never a 500) — surface it as a soft inline
        // 'temporarily unavailable' state rather than silently looking like
        // "no signals ever fired on this ticker".
        if (r.data?.error) setUnavailable(true)
      })
      .catch(() => { setSignals([]); setTimeline([]); setMomentum(null); setUnavailable(true) })
      .finally(() => setLoading(false))
  }, [ticker, days, retryKey])

  const uniqueSources = Array.from(new Set(signals.map(s => s.source))).sort()
  const visible = sourceFilter ? signals.filter(s => s.source === sourceFilter) : signals

  const sentColor = (s: number | null) => {
    if (s == null) return 'text-text-muted'
    if (s > 0.3) return 'text-emerald-300'
    if (s > 0.1) return 'text-emerald-400/80'
    if (s < -0.3) return 'text-rose-300'
    if (s < -0.1) return 'text-rose-400/80'
    return 'text-text-muted'
  }

  return (
    <div className="space-y-4">
      <SimilarTickersPanel ticker={ticker} />
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold text-text-primary flex items-center gap-2">
            <Radio size={16} className="text-blue-light" />
            Signal History — {ticker}
          </h2>
          <p className="text-xs text-text-muted mt-1">
            Every signal any originator fired on this ticker in the last {days} days.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {unavailable && (
            <button
              type="button"
              onClick={() => setRetryKey(k => k + 1)}
              className="text-xs px-2 py-1 rounded border border-amber-500/40 bg-amber-500/10 text-amber-300 hover:opacity-80"
            >
              Signal history temporarily unavailable — retry
            </button>
          )}
          {[7, 30, 90].map(d => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={clsx(
                'px-2 py-1 text-xs rounded border',
                days === d
                  ? 'border-blue bg-blue/20 text-blue-light'
                  : 'border-border text-text-muted hover:text-text-primary',
              )}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* Signal-momentum strip — is attention accelerating or fading? */}
      {momentum != null && (
        <div
          className={clsx(
            'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-medium',
            momentum.direction === 'accelerating'
              ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
              : momentum.direction === 'fading'
                ? 'border-rose-500/40 bg-rose-500/10 text-rose-300'
                : 'border-border bg-surface-hover text-text-secondary',
          )}
          title={momentum.note ?? ''}
        >
          <span>
            {momentum.direction === 'accelerating' ? '▲' : momentum.direction === 'fading' ? '▼' : '◆'}
          </span>
          <span className="capitalize">{momentum.direction}</span>
          <span className="opacity-70">{(momentum.volume_change_pct ?? 0) >= 0 ? '+' : ''}{momentum.volume_change_pct ?? 0}%</span>
        </div>
      )}

      {/* Daily timeline sparkbar */}
      {timeline.length > 0 && (
        <div className="card p-4">
          <div className="text-xs text-text-muted mb-2">Daily sentiment volume (green = bull, red = bear)</div>
          <div className="flex items-end gap-0.5 h-16">
            {timeline.map(d => {
              const count = d.count ?? 0
              const bull = d.bull ?? 0
              const bear = d.bear ?? 0
              const max = Math.max(...timeline.map(x => x.count ?? 0), 1)
              const h = (count / max) * 100
              const bullH = (bull / Math.max(count, 1)) * h
              const bearH = (bear / Math.max(count, 1)) * h
              return (
                <div key={d.date} className="flex-1 flex flex-col justify-end" title={`${d.date}: ${count} signals · bull ${bull} · bear ${bear} · avg sent ${(d.avg_sent ?? 0).toFixed(2)}`}>
                  <div className="bg-rose-500/50" style={{ height: `${bearH}%` }} />
                  <div className="bg-emerald-500/50" style={{ height: `${bullH}%` }} />
                </div>
              )
            })}
          </div>
          <div className="mt-1 flex justify-between text-[9px] text-text-muted">
            <span>{timeline[0]?.date}</span>
            <span>{timeline[timeline.length - 1]?.date}</span>
          </div>
        </div>
      )}

      {/* Source filter chips */}
      {uniqueSources.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => setSourceFilter(null)}
            className={clsx('text-[10px] px-2 py-0.5 rounded border',
              sourceFilter === null
                ? 'border-blue text-blue-light bg-blue/15'
                : 'border-border text-text-muted hover:text-text-primary')}
          >
            All ({signals.length})
          </button>
          {uniqueSources.map(src => {
            const n = signals.filter(s => s.source === src).length
            return (
              <button
                key={src}
                type="button"
                onClick={() => setSourceFilter(sourceFilter === src ? null : src)}
                className={clsx('text-[10px] px-2 py-0.5 rounded border font-mono',
                  sourceFilter === src
                    ? 'border-blue text-blue-light bg-blue/15'
                    : 'border-border text-text-muted hover:text-text-primary')}
              >
                {src} ({n})
              </button>
            )
          })}
        </div>
      )}

      {/* Signal list */}
      {loading ? (
        <div className="space-y-2">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-14 rounded bg-surface-hover/40 animate-pulse" />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <div className="card p-8 text-center text-sm text-text-muted">
          {unavailable
            ? `Signal history temporarily unavailable for ${ticker} — try again shortly.`
            : `No signals fired on ${ticker} in the last ${days} days.`}
        </div>
      ) : (
        <div className="card p-0 overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-surface-hover/40 border-b border-border">
              <tr className="text-text-muted uppercase tracking-wider">
                <th className="px-3 py-2 text-left">When</th>
                <th className="px-3 py-2 text-left">Source</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-right">Sent</th>
                <th className="px-3 py-2 text-right">Conf</th>
                <th className="px-3 py-2 text-left">Summary</th>
              </tr>
            </thead>
            <tbody>
              {visible.slice(0, 300).map((s, i) => (
                <tr key={i} className="border-b border-border/50 hover:bg-surface-hover/30">
                  <td className="px-3 py-2 text-text-muted font-mono text-[10px] whitespace-nowrap">
                    {s.created_at ? new Date(s.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                  </td>
                  <td className="px-3 py-2 text-text-secondary font-mono text-[10px]">{s.source}</td>
                  <td className="px-3 py-2 text-text-muted text-[10px]">{s.signal_type}</td>
                  <td className={clsx('px-3 py-2 text-right font-mono tabular-nums', sentColor(s.sentiment))}>
                    {s.sentiment != null ? s.sentiment.toFixed(2) : '—'}
                  </td>
                  <td className="px-3 py-2 text-right font-mono tabular-nums text-text-muted">
                    {s.confidence != null ? s.confidence.toFixed(2) : '—'}
                  </td>
                  <td className="px-3 py-2 text-text-primary">
                    {s.summary ?? s.detail ?? '—'}
                    {s.raw_url && (
                      <a href={s.raw_url} target="_blank" rel="noreferrer" className="ml-2 text-blue-light hover:underline inline-flex items-center gap-0.5 text-[10px]">
                        <ExternalLink size={9} /> source
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {visible.length > 300 && (
            <div className="text-center py-2 text-[10px] text-text-muted bg-surface-hover/20">
              Showing first 300 of {visible.length} signals.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── News Tab ──────────────────────────────────────────────────────────────────

function NewsTab({ news }: { news: import('@/lib/types').StockDetailNewsItem[] }) {
  return (
    <div className="space-y-3">
      {news.length === 0 ? (
        <div className="text-text-muted text-sm text-center py-8">No recent news found.</div>
      ) : (
        news.map((item, i) => (
          <a
            key={i}
            href={item.url || '#'}
            target={item.url ? '_blank' : '_self'}
            rel="noopener noreferrer"
            className="block card p-4 hover:border-blue/40 transition-colors group"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-text-primary group-hover:text-cyan-300 transition-colors leading-snug">
                  {item.title}
                </p>
                <div className="flex items-center gap-3 mt-1.5">
                  {item.source && (
                    <span className="text-[11px] text-text-muted">{item.source}</span>
                  )}
                  {item.published_at && (
                    <span className="text-[11px] text-text-muted">
                      {fmtDate(item.published_at)}
                    </span>
                  )}
                </div>
              </div>
              {item.url && (
                <ExternalLink size={14} className="text-text-muted group-hover:text-cyan-400 shrink-0 mt-0.5 transition-colors" />
              )}
            </div>
          </a>
        ))
      )}
    </div>
  )
}

// ── Quick Action Bar ──────────────────────────────────────────────────────────

function QuickActionBar({ ticker, companyName }: { ticker: string; companyName?: string }) {
  const navigate = useNavigate()
  const { addItem, items } = useWatchlist()
  const [added, setAdded] = useState(false)

  const isWatched = items.some(i => i.ticker.toUpperCase() === ticker.toUpperCase())

  async function handleWatchlist() {
    if (isWatched || added) return
    await addItem({ ticker: ticker.toUpperCase(), name: companyName ?? ticker.toUpperCase() })
    setAdded(true)
    setTimeout(() => setAdded(false), 2500)
  }

  function handleAnalyze() {
    navigate('/app/terminal', { state: { initialCommand: `/quote ${ticker.toUpperCase()}` } })
  }

  function handleSize() {
    navigate('/app/terminal', { state: { initialCommand: `/size ${ticker.toUpperCase()}` } })
  }

  function handleCompare() {
    navigate(`/app/chart?ticker=${encodeURIComponent(ticker.toUpperCase())}`)
  }

  const watchLabel = isWatched || added ? 'Watching' : 'Add to Watchlist'
  const WatchIcon  = isWatched || added ? Check : BookmarkPlus

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <button
        type="button"
        onClick={handleWatchlist}
        disabled={isWatched || added}
        className={clsx(
          'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors',
          isWatched || added
            ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-400 cursor-default'
            : 'bg-surface-card border-border text-text-secondary hover:border-blue/50 hover:text-text-primary',
        )}
        aria-label={watchLabel}
      >
        <WatchIcon size={13} aria-hidden />
        {watchLabel}
      </button>

      <button
        type="button"
        onClick={handleAnalyze}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-border bg-surface-card text-text-secondary hover:border-blue/50 hover:text-text-primary transition-colors"
        aria-label={`Open ${ticker} in terminal`}
      >
        <Terminal size={13} aria-hidden />
        Analyze
      </button>

      <button
        type="button"
        onClick={handleSize}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-border bg-surface-card text-text-secondary hover:border-blue/50 hover:text-text-primary transition-colors"
        aria-label={`Size position for ${ticker}`}
      >
        <SlidersHorizontal size={13} aria-hidden />
        Size Position
      </button>

      <button
        type="button"
        onClick={handleCompare}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-border bg-surface-card text-text-secondary hover:border-blue/50 hover:text-text-primary transition-colors"
        aria-label={`Compare ${ticker} on chart`}
      >
        <GitCompare size={13} aria-hidden />
        Compare
      </button>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

interface ProxySuggestion {
  ticker: string
  tradeable_directly?: boolean
  region?: 'CN' | 'KR' | 'TW'
  adr?: string | null
  adr_note?: string
  etf?: string | null
  alt_etfs?: string[]
  rationale?: string
}

function ProxyBanner({ proxy }: { proxy: ProxySuggestion | null }) {
  if (!proxy || proxy.tradeable_directly) return null
  const primary = proxy.adr || proxy.etf
  if (!primary) return null
  return (
    <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[11px] uppercase tracking-wider text-amber-300 font-bold">
          Not UK-retail tradeable
        </span>
        <span className="text-xs text-text-muted">—</span>
        <span className="text-xs text-text-secondary">
          Trade via
        </span>
        {proxy.adr && (
          <Link
            to={`/app/stock/${encodeURIComponent(proxy.adr)}`}
            className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border bg-emerald-500/15 text-emerald-300 border-emerald-500/40 font-bold hover:bg-emerald-500/25 transition"
          >
            {proxy.adr} (ADR){proxy.adr_note ? ` · ${proxy.adr_note}` : ''}
          </Link>
        )}
        {proxy.etf && (
          <Link
            to={`/app/stock/${encodeURIComponent(proxy.etf)}`}
            className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border bg-blue-500/15 text-blue-300 border-blue-500/40 font-bold hover:bg-blue-500/25 transition"
          >
            {proxy.etf} (ETF)
          </Link>
        )}
        {(proxy.alt_etfs ?? []).length > 0 && (
          <span className="text-[11px] text-text-muted">
            alts: {proxy.alt_etfs!.join(', ')}
          </span>
        )}
      </div>
      {proxy.rationale && (
        <p className="text-[11px] text-text-secondary mt-1 leading-snug">
          {proxy.rationale}
        </p>
      )}
    </div>
  )
}

function RegimeChip({ ticker }: { ticker: string }) {
  const [data, setData] = useState<{
    archetype: string
    impact: 'supportive' | 'headwind' | 'neutral' | 'mixed'
    regime_score: number
    regime_direction: 'risk_on' | 'risk_off' | 'neutral'
  } | null>(null)

  useEffect(() => {
    if (!ticker) return
    let cancelled = false
    ;(async () => {
      try {
        const { api } = await import('@/lib/api')
        const res = await api.get(`/api/macro/regime/ticker/${encodeURIComponent(ticker)}`)
        if (!cancelled) setData(res.data)
      } catch {
        if (!cancelled) setData(null)
      }
    })()
    return () => { cancelled = true }
  }, [ticker])

  if (!data) return null
  if (data.regime_direction === 'neutral') return null  // nothing interesting

  const colour =
    data.impact === 'supportive' ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
    : data.impact === 'headwind' ? 'border-rose-500/40 bg-rose-500/10 text-rose-300'
    : data.impact === 'mixed' ? 'border-amber-500/40 bg-amber-500/10 text-amber-300'
    : 'border-border bg-surface-hover text-text-secondary'

  return (
    <div className={`mt-3 rounded-lg border px-3 py-2 ${colour}`}>
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <span className="text-[11px] uppercase tracking-wider font-bold">
          Regime context
        </span>
        <span>·</span>
        <span className="font-mono">
          {data.regime_score >= 0 ? '+' : ''}{data.regime_score.toFixed(2)}
        </span>
        <span className="capitalize">
          {data.regime_direction.replace('_', ' ')}
        </span>
        <span>·</span>
        <span>archetype: <span className="font-semibold capitalize">{data.archetype.replace(/_/g, ' ')}</span></span>
        <span>·</span>
        <span className="font-semibold uppercase">{data.impact}</span>
        <Link to="/app/regime" className="ml-auto text-[10px] underline opacity-70 hover:opacity-100">
          regime dashboard →
        </Link>
      </div>
    </div>
  )
}

export default function StockDetailPage() {
  const { ticker } = useParams<{ ticker: string }>()
  const navigate = useNavigate()
  // Honour ?tab=... query param so deep links from trade-ideas / blotter
  // / dashboard can drop the user straight on the right tab.
  const initialTab = (() => {
    try {
      const qp = new URLSearchParams(window.location.search).get('tab')
      if (qp && (TABS as readonly string[]).includes(qp)) return qp as Tab
    } catch { /* SSR fallback */ }
    return 'Overview' as Tab
  })()
  const [activeTab, setActiveTab] = useState<Tab>(initialTab)
  const [proxy, setProxy] = useState<ProxySuggestion | null>(null)
  const { data, loading, error, refetch } = useStockDetail(ticker ?? '')
  const {
    data: financials,
    loading: financialsLoading,
    error: financialsError,
  } = useStockFinancials(activeTab === 'Financials' ? (ticker ?? '') : '')
  const { addTicker } = useRecentActivity()
  const { fetchWatchlist } = useWatchlist()

  // Track recently viewed tickers
  useEffect(() => {
    if (ticker) addTicker(ticker)
  }, [ticker]) // eslint-disable-line

  // Ensure watchlist items are loaded so QuickActionBar can detect duplicates
  useEffect(() => {
    fetchWatchlist()
  }, []) // eslint-disable-line

  // Reset tab when ticker changes — but respect an explicit ?tab=Signals
  // if it's in the URL (deep-link from trade-ideas).
  useEffect(() => {
    try {
      const qp = new URLSearchParams(window.location.search).get('tab')
      if (qp && (TABS as readonly string[]).includes(qp)) {
        setActiveTab(qp as Tab)
        return
      }
    } catch { /* noop */ }
    setActiveTab('Overview')
  }, [ticker])

  // Fetch proxy suggestion — returns tradeable_directly:true for normal
  // tickers (banner simply doesn't render) or CN/KR/.TWO proxies.
  useEffect(() => {
    if (!ticker) return
    let cancelled = false
    ;(async () => {
      try {
        const { api } = await import('@/lib/api')
        const res = await api.get(`/api/winners/proxy/${encodeURIComponent(ticker)}`)
        if (!cancelled) setProxy(res.data as ProxySuggestion)
      } catch {
        if (!cancelled) setProxy(null)
      }
    })()
    return () => { cancelled = true }
  }, [ticker])

  if (!ticker) {
    return (
      <div className="p-8 text-center text-text-muted">No ticker specified.</div>
    )
  }

  // ── Header ──────────────────────────────────────────────────────────────────

  const quote = data?.quote
  const profile = data?.profile

  return (
    <div className="min-h-full bg-base">
      {/* Top bar */}
      <div className="sticky top-0 z-10 bg-surface-DEFAULT/95 backdrop-blur border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-3">
          {/* Navigation row */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-1.5 text-sm text-text-muted hover:text-text-primary transition-colors"
            >
              <ArrowLeft size={16} />
              Back
            </button>
            <button
              onClick={refetch}
              disabled={loading}
              className="flex items-center gap-1.5 text-sm text-text-muted hover:text-text-primary transition-colors disabled:opacity-50"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>

          {/* Ticker / price header */}
          <div className="flex flex-col sm:flex-row sm:items-end gap-2 sm:gap-6">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold font-mono text-text-primary">{ticker.toUpperCase()}</h1>
                {quote?.currency && quote.currency !== 'USD' && (
                  <span className="text-xs bg-surface-card border border-border rounded px-1.5 py-0.5 text-text-muted">
                    {quote.currency}
                  </span>
                )}
              </div>
              {profile?.name && profile.name !== ticker.toUpperCase() && (
                <div className="text-sm text-text-muted mt-0.5">{profile.name}</div>
              )}
              {profile?.sector && (
                <div className="text-xs text-text-muted mt-0.5">{profile.sector} · {profile.industry}</div>
              )}
            </div>

            {quote?.price != null && (
              <div className="flex items-end gap-3">
                <div className="stat-hero tabular font-mono text-text-primary">
                  {fmtPrice(quote.price, quote.currency)}
                </div>
                {(quote.change != null || quote.change_pct != null) && (
                  <span className={clsx(
                    'mb-2',
                    (quote.change ?? quote.change_pct ?? 0) >= 0 ? 'delta-chip-pos' : 'delta-chip-neg',
                  )}>
                    {(quote.change ?? quote.change_pct ?? 0) >= 0
                      ? <TrendingUp size={11} aria-hidden />
                      : <TrendingDown size={11} aria-hidden />}
                    {quote.change != null && (
                      <span>{(quote.change >= 0 ? '+' : '') + quote.change.toFixed(2)}</span>
                    )}
                    {quote.change_pct != null && (
                      <span>{quote.change != null ? `(${fmtPct(quote.change_pct)})` : fmtPct(quote.change_pct)}</span>
                    )}
                  </span>
                )}
              </div>
            )}

            {/* Position-aware portfolio fit — qualifies any bullish rating by
                current book weight (e.g. "⚠ OVERWEIGHT · TRIM"). */}
            {data?.portfolio_fit?.action && (
              <div className="sm:ml-auto sm:self-center">
                <PortfolioFitBadge fit={data.portfolio_fit} />
              </div>
            )}
          </div>

          {/* Fuller one-line reason for the fit verdict. */}
          <PortfolioFitNote fit={data?.portfolio_fit} />

          {/* Proxy banner — shows for non-UK-tradeable tickers (CN/KR/.TWO) */}
          <ProxyBanner proxy={proxy} />

          {/* Regime impact chip for this ticker */}
          <RegimeChip ticker={ticker} />

          {/* Quick action bar */}
          <div className="mt-3">
            <QuickActionBar
              ticker={ticker}
              companyName={profile?.name}
            />
          </div>

          {/* Active Blotter Idea banner — if this ticker has a live idea */}
          <ActiveIdeaBanner ticker={ticker} />

          {/* Tabs */}
          <div className="flex gap-1 mt-3 -mb-px overflow-x-auto scrollbar-none">
            {TABS.map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={clsx(
                  'flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 whitespace-nowrap transition-colors',
                  activeTab === tab
                    ? 'border-blue text-blue-light'
                    : 'border-transparent text-text-muted hover:text-text-secondary',
                )}
              >
                {tab === 'Overview' && <BarChart2 size={13} />}
                {tab === 'Technicals' && <Activity size={13} />}
                {tab === 'Fundamentals' && <Building2 size={13} />}
                {tab === 'Earnings' && <DollarSign size={13} />}
                {tab === 'Financials' && <FileText size={13} />}
                {tab === 'Alt Data' && <Eye size={13} />}
                {tab === 'Signals' && <Radio size={13} />}
                {tab === 'News' && <Newspaper size={13} />}
                {tab}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {loading && !data && (
          <div className="flex flex-col items-center justify-center gap-3 py-20 text-text-muted">
            <Loader2 className="animate-spin" size={32} />
            <p className="text-sm">Loading {ticker.toUpperCase()} data…</p>
          </div>
        )}

        {error && !data && (
          <div className="flex flex-col items-center justify-center gap-3 py-20">
            <AlertTriangle size={32} className="text-amber-400" />
            <p className="text-sm text-text-muted">{error}</p>
            <button
              onClick={refetch}
              className="px-4 py-2 bg-blue/20 hover:bg-blue/30 text-blue-light rounded-lg text-sm transition-colors"
            >
              Retry
            </button>
          </div>
        )}

        {data && (
          <>
            {activeTab === 'Overview' && (
              <OverviewTab
                ticker={ticker}
                quote={data.quote}
                ratings={data.ratings}
                peers={data.peers}
              />
            )}

            {activeTab === 'Technicals' && (
              <TechnicalsTab technicals={data.technicals} />
            )}

            {activeTab === 'Fundamentals' && (
              <FundamentalsTab profile={data.profile} quote={data.quote} />
            )}

            {activeTab === 'Earnings' && (
              <EarningsTab earnings={data.earnings} />
            )}

            {activeTab === 'Financials' && (
              <FinancialsTab
                ticker={ticker}
                financials={financials}
                loading={financialsLoading}
                error={financialsError}
              />
            )}

            {activeTab === 'Catalysts' && (
              <CatalystsTab ticker={ticker} />
            )}

            {activeTab === 'Alt Data' && (
              <AltDataTab data={data} />
            )}

            {activeTab === 'Signals' && (
              <SignalsHistoryTab ticker={ticker} />
            )}

            {activeTab === 'News' && (
              <NewsTab news={data.news} />
            )}

            {/* Loading overlay for refreshes */}
            {loading && (
              <div className="fixed bottom-4 right-4 flex items-center gap-2 bg-surface-card border border-border rounded-lg px-3 py-2 text-xs text-text-muted shadow-lg">
                <Loader2 size={12} className="animate-spin" />
                Refreshing…
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
